/**
 * Apartment Complex — walkable hallway with 30 rentable rooms (100 VC/week),
 * gold name plaques, interiors (bedroom/bathroom/kitchen/living), furniture
 * shop + room editor, and knock-to-visit.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { X, Coins, DoorOpen, Sofa, Pencil, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, RefreshCw, LogOut, Crown } from 'lucide-react';
import { drawPlayerSprite, computeTint, resolveGender, ensureSpriteLoaded, directionFromDelta } from './playerSprite';
import { drawOutfitLayers } from './outfitLayers';
import { drawSpecialSprite, isSheetSpecial } from './specialSprites';
import { RARITY_META } from './outfitItems';
import { DANCE_EMOTES } from './constants';
import { drawChatBubble } from './chatOverlay';

const EMOTE_ICONS = Object.fromEntries(DANCE_EMOTES.map((e) => [e.id, e.icon || e.emoji]));

const W = 800;
const BASE_H = 520;
const WORLD_H = 2480;
const DOOR_ROWS = 15;
const ROW_START = 260;
const ROW_SPACING = 146;
const CORRIDOR = { minX: 235, maxX: 565 };

export const FURNITURE = [
  { id: 'rug_purple', name: 'Purple Rug', price: 30, rarity: 'common' },
  { id: 'rug_zebra', name: 'Zebra Rug', price: 45, rarity: 'rare' },
  { id: 'plant_palm', name: 'Palm Plant', price: 40, rarity: 'common' },
  { id: 'plant_monstera', name: 'Monstera', price: 35, rarity: 'common' },
  { id: 'lamp_floor', name: 'Floor Lamp', price: 50, rarity: 'common' },
  { id: 'lamp_lava', name: 'Lava Lamp', price: 90, rarity: 'rare' },
  { id: 'sofa_gray', name: 'Gray Sofa', price: 80, rarity: 'common' },
  { id: 'sofa_velvet', name: 'Velvet Sofa', price: 150, rarity: 'epic' },
  { id: 'table_coffee', name: 'Coffee Table', price: 60, rarity: 'common' },
  { id: 'bookshelf', name: 'Bookshelf', price: 70, rarity: 'rare' },
  { id: 'tv_flat', name: 'Flat-Screen TV', price: 120, rarity: 'epic' },
  { id: 'gaming_setup', name: 'Gaming Setup', price: 200, rarity: 'epic' },
  { id: 'neon_heart', name: 'Neon Heart Sign', price: 100, rarity: 'rare' },
  { id: 'aquarium', name: 'Aquarium', price: 180, rarity: 'epic' },
  { id: 'disco_ball', name: 'Disco Ball', price: 140, rarity: 'epic' },
  { id: 'mini_bar', name: 'Mini Bar', price: 160, rarity: 'epic' },
  { id: 'jukebox', name: 'Jukebox', price: 220, rarity: 'legendary' },
  { id: 'throne_gold', name: 'Gold Throne', price: 300, rarity: 'legendary' },
  { id: 'wall_clock', name: 'Wall Clock', price: 25, rarity: 'common' },
  { id: 'beanbag', name: 'Beanbag Chair', price: 35, rarity: 'common' },
  { id: 'cactus_pot', name: 'Potted Cactus', price: 30, rarity: 'common' },
  { id: 'standing_mirror', name: 'Standing Mirror', price: 50, rarity: 'common' },
  { id: 'bar_cart', name: 'Gold Bar Cart', price: 95, rarity: 'rare' },
  { id: 'record_player', name: 'Record Player', price: 110, rarity: 'rare' },
  { id: 'punching_bag', name: 'Punching Bag', price: 120, rarity: 'rare' },
  { id: 'electric_fireplace', name: 'Electric Fireplace', price: 130, rarity: 'rare' },
  { id: 'indoor_fountain', name: 'Indoor Fountain', price: 190, rarity: 'epic' },
  { id: 'arcade_machine', name: 'Arcade Machine', price: 210, rarity: 'epic' },
  { id: 'pool_table', name: 'Pool Table', price: 230, rarity: 'epic' },
  { id: 'home_cinema', name: 'Home Cinema', price: 240, rarity: 'epic' },
  { id: 'grand_piano', name: 'Grand Piano', price: 320, rarity: 'legendary' },
  { id: 'neon_ceiling', name: 'Neon Halo Rig', price: 350, rarity: 'legendary' },
  { id: 'gold_statue', name: 'Golden Statue', price: 400, rarity: 'legendary' },
  { id: 'rug_rainbow', name: 'Rainbow Rug', price: 35, rarity: 'common' },
  { id: 'plant_bonsai', name: 'Bonsai Tree', price: 45, rarity: 'common' },
  { id: 'floor_pouf', name: 'Velvet Pouf', price: 30, rarity: 'common' },
  { id: 'wall_art', name: 'Abstract Wall Art', price: 40, rarity: 'common' },
  { id: 'string_lights', name: 'String Lights', price: 45, rarity: 'common' },
  { id: 'side_table', name: 'Marble Side Table', price: 50, rarity: 'common' },
  { id: 'coat_rack', name: 'Coat Rack', price: 28, rarity: 'common' },
  { id: 'floor_candles', name: 'Candle Trio', price: 38, rarity: 'common' },
  { id: 'skate_rack', name: 'Skateboard Rack', price: 55, rarity: 'rare' },
  { id: 'neon_turtle', name: 'Neon Turtle Sign', price: 115, rarity: 'rare' },
  { id: 'surfboard', name: 'Surfboard', price: 85, rarity: 'rare' },
  { id: 'drum_kit', name: 'Drum Kit', price: 140, rarity: 'rare' },
  { id: 'hammock', name: 'Hammock', price: 95, rarity: 'rare' },
  { id: 'vinyl_wall', name: 'Vinyl Record Wall', price: 105, rarity: 'rare' },
  { id: 'slot_machine', name: 'Slot Machine', price: 170, rarity: 'epic' },
  { id: 'dj_booth_mini', name: 'Mini DJ Booth', price: 190, rarity: 'epic' },
  { id: 'claw_machine', name: 'Claw Machine', price: 185, rarity: 'epic' },
  { id: 'vending_machine', name: 'Soda Vending Machine', price: 150, rarity: 'epic' },
  { id: 'hot_tub_mini', name: 'Mini Hot Tub', price: 250, rarity: 'epic' },
  { id: 'terrarium', name: 'Turtle Terrarium', price: 175, rarity: 'epic' },
  { id: 'pinball', name: 'Pinball Machine', price: 205, rarity: 'epic' },
  { id: 'ice_sculpture', name: 'Ice Sculpture', price: 260, rarity: 'legendary' },
  { id: 'koi_pond', name: 'Koi Pond', price: 330, rarity: 'legendary' },
  { id: 'crystal_chandelier', name: 'Crystal Chandelier', price: 310, rarity: 'legendary' },
  { id: 'money_pile', name: 'Money Pile', price: 380, rarity: 'legendary' },
];

const SUB_ROOMS = {
  bedroom: { name: 'Bedroom', wall: '#3d2f4f', floor: '#2a2138' },
  bathroom: { name: 'Bathroom', wall: '#1e3a45', floor: '#16303a' },
  kitchen: { name: 'Kitchen', wall: '#453a24', floor: '#332b1a' },
  living: { name: 'Living Room', wall: '#31343f', floor: '#23252e' },
  penthouse_main: { name: '👑 Penthouse — Main Floor', wall: '#1f1830', floor: '#16101f' },
  penthouse_upper: { name: '👑 Penthouse — Upstairs', wall: '#261a38', floor: '#1a1128' },
};

// door row/side for a room number (1-30): odd = left, even = right
const doorPos = (n) => {
  const row = Math.floor((n - 1) / 2);
  const y = ROW_START + row * ROW_SPACING;
  const left = n % 2 === 1;
  return { x: left ? 118 : 682, y, left };
};

// cozy hot-tub seat spots (penthouse main floor)
const TUB_SEATS = [{ x: 138, y: 404 }, { x: 232, y: 404 }, { x: 185, y: 424 }];

// ==================== FURNITURE DRAWING ====================
export function drawFurniture(ctx, id, x, y, phase, highlight) {
  ctx.save();
  const t = phase * 0.05;
  if (highlight) {
    ctx.shadowColor = '#fbbf24';
    ctx.shadowBlur = 14;
  }
  switch (id) {
    case 'rug_purple':
      ctx.fillStyle = '#7c3aed';
      ctx.beginPath(); ctx.ellipse(x, y, 55, 20, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.ellipse(x, y, 42, 14, 0, 0, Math.PI * 2); ctx.stroke();
      break;
    case 'rug_zebra': {
      ctx.fillStyle = '#e5e7eb';
      ctx.beginPath(); ctx.ellipse(x, y, 55, 20, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#111827';
      for (let i = 0; i < 6; i++) {
        ctx.save();
        ctx.translate(x - 40 + i * 16, y);
        ctx.rotate(0.35);
        ctx.fillRect(-3, -14, 6, 28);
        ctx.restore();
      }
      break;
    }
    case 'plant_palm':
      ctx.fillStyle = '#92400e'; ctx.fillRect(x - 10, y - 14, 20, 14);
      ctx.strokeStyle = '#16a34a'; ctx.lineWidth = 4;
      [[-1, -0.6], [0, -1.2], [1, -0.6]].forEach(([d]) => {
        ctx.beginPath();
        ctx.moveTo(x, y - 12);
        ctx.quadraticCurveTo(x + d * 18, y - 44, x + d * 30, y - 34);
        ctx.stroke();
      });
      break;
    case 'plant_monstera':
      ctx.fillStyle = '#b45309'; ctx.fillRect(x - 9, y - 12, 18, 12);
      ctx.fillStyle = '#15803d';
      [[-12, -30], [0, -40], [12, -28]].forEach(([ox, oy]) => {
        ctx.beginPath(); ctx.ellipse(x + ox, y + oy, 10, 13, ox * 0.02, 0, Math.PI * 2); ctx.fill();
      });
      break;
    case 'lamp_floor':
      ctx.fillStyle = '#4b5563'; ctx.fillRect(x - 2, y - 52, 4, 52);
      ctx.fillStyle = '#fde68a';
      ctx.beginPath(); ctx.moveTo(x - 14, y - 52); ctx.lineTo(x + 14, y - 52); ctx.lineTo(x + 9, y - 70); ctx.lineTo(x - 9, y - 70); ctx.closePath(); ctx.fill();
      ctx.fillStyle = 'rgba(253, 230, 138, 0.14)';
      ctx.beginPath(); ctx.arc(x, y - 40, 34, 0, Math.PI * 2); ctx.fill();
      break;
    case 'lamp_lava': {
      ctx.fillStyle = '#111827'; ctx.fillRect(x - 8, y - 6, 16, 6);
      const lg = ctx.createLinearGradient(x, y - 46, x, y - 6);
      lg.addColorStop(0, '#f472b6'); lg.addColorStop(1, '#7c3aed');
      ctx.fillStyle = lg;
      ctx.beginPath(); ctx.moveTo(x - 9, y - 6); ctx.lineTo(x + 9, y - 6); ctx.lineTo(x + 5, y - 46); ctx.lineTo(x - 5, y - 46); ctx.closePath(); ctx.fill();
      ctx.fillStyle = 'rgba(255, 200, 255, 0.85)';
      for (let i = 0; i < 3; i++) {
        const by = y - 14 - ((t * 12 + i * 13) % 28);
        ctx.beginPath(); ctx.arc(x + Math.sin(t + i) * 3, by, 3, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'sofa_gray':
    case 'sofa_velvet': {
      const c = id === 'sofa_velvet' ? '#9d174d' : '#4b5563';
      const cl = id === 'sofa_velvet' ? '#be185d' : '#6b7280';
      ctx.fillStyle = c; ctx.fillRect(x - 45, y - 30, 90, 30);
      ctx.fillStyle = cl; ctx.fillRect(x - 45, y - 42, 90, 14);
      ctx.fillRect(x - 52, y - 36, 9, 36); ctx.fillRect(x + 43, y - 36, 9, 36);
      ctx.fillStyle = 'rgba(0,0,0,0.2)'; ctx.fillRect(x - 40, y - 26, 80, 4);
      break;
    }
    case 'table_coffee':
      ctx.fillStyle = '#78350f'; ctx.fillRect(x - 34, y - 18, 68, 8);
      ctx.fillRect(x - 30, y - 10, 6, 10); ctx.fillRect(x + 24, y - 10, 6, 10);
      ctx.fillStyle = '#fbbf24'; ctx.fillRect(x - 8, y - 24, 16, 6);
      break;
    case 'bookshelf': {
      ctx.fillStyle = '#5b3a1e'; ctx.fillRect(x - 26, y - 70, 52, 70);
      const cols = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7'];
      for (let s = 0; s < 3; s++) {
        ctx.fillStyle = '#3f2a15'; ctx.fillRect(x - 22, y - 60 + s * 20, 44, 3);
        for (let b = 0; b < 5; b++) {
          ctx.fillStyle = cols[(b + s) % cols.length];
          ctx.fillRect(x - 20 + b * 8, y - 58 + s * 20 + 3, 6, 14);
        }
      }
      break;
    }
    case 'tv_flat': {
      ctx.fillStyle = '#111827'; ctx.fillRect(x - 40, y - 56, 80, 46);
      const tg = ctx.createLinearGradient(x - 36, y - 52, x + 36, y - 14);
      tg.addColorStop(0, `hsl(${(phase * 0.5) % 360}, 60%, 30%)`);
      tg.addColorStop(1, `hsl(${(phase * 0.5 + 120) % 360}, 60%, 20%)`);
      ctx.fillStyle = tg; ctx.fillRect(x - 36, y - 52, 72, 38);
      ctx.fillStyle = '#374151'; ctx.fillRect(x - 6, y - 10, 12, 6); ctx.fillRect(x - 16, y - 4, 32, 4);
      break;
    }
    case 'gaming_setup': {
      ctx.fillStyle = '#1f2937'; ctx.fillRect(x - 36, y - 24, 72, 8);
      ctx.fillRect(x - 32, y - 16, 6, 16); ctx.fillRect(x + 26, y - 16, 6, 16);
      ctx.fillStyle = '#111827'; ctx.fillRect(x - 26, y - 52, 52, 30);
      const glow = `hsl(${(phase * 1.2) % 360}, 90%, 55%)`;
      ctx.strokeStyle = glow; ctx.lineWidth = 2; ctx.strokeRect(x - 26, y - 52, 52, 30);
      ctx.fillStyle = glow; ctx.globalAlpha = 0.5; ctx.fillRect(x - 22, y - 48, 44, 22); ctx.globalAlpha = 1;
      ctx.fillStyle = '#374151'; ctx.fillRect(x - 14, y - 22, 28, 5);
      break;
    }
    case 'neon_heart': {
      const pulse = 0.7 + Math.abs(Math.sin(t * 2)) * 0.3;
      ctx.strokeStyle = `rgba(244, 63, 94, ${pulse})`;
      ctx.shadowColor = '#f43f5e'; ctx.shadowBlur = 16 * pulse; ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x, y - 20);
      ctx.bezierCurveTo(x - 22, y - 42, x - 30, y - 18, x, y - 4);
      ctx.bezierCurveTo(x + 30, y - 18, x + 22, y - 42, x, y - 20);
      ctx.stroke();
      break;
    }
    case 'aquarium': {
      ctx.fillStyle = '#374151'; ctx.fillRect(x - 38, y - 8, 76, 8);
      const wg = ctx.createLinearGradient(x, y - 48, x, y - 8);
      wg.addColorStop(0, '#0ea5e9'); wg.addColorStop(1, '#0c4a6e');
      ctx.fillStyle = wg; ctx.fillRect(x - 34, y - 48, 68, 40);
      ctx.strokeStyle = '#93c5fd'; ctx.strokeRect(x - 34, y - 48, 68, 40);
      for (let i = 0; i < 3; i++) {
        const fx = x - 24 + ((t * 18 + i * 27) % 48);
        const fy = y - 38 + (i * 11) % 24 + Math.sin(t * 2 + i) * 3;
        ctx.fillStyle = ['#fb923c', '#fde047', '#f472b6'][i];
        ctx.beginPath(); ctx.ellipse(fx, fy, 4, 2.5, 0, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.moveTo(fx - 4, fy); ctx.lineTo(fx - 8, fy - 3); ctx.lineTo(fx - 8, fy + 3); ctx.fill();
      }
      break;
    }
    case 'disco_ball': {
      ctx.strokeStyle = '#9ca3af'; ctx.beginPath(); ctx.moveTo(x, y - 78); ctx.lineTo(x, y - 62); ctx.stroke();
      const dg = ctx.createRadialGradient(x - 4, y - 52, 2, x, y - 48, 15);
      dg.addColorStop(0, '#f9fafb'); dg.addColorStop(1, '#6b7280');
      ctx.fillStyle = dg;
      ctx.beginPath(); ctx.arc(x, y - 48, 14, 0, Math.PI * 2); ctx.fill();
      for (let i = 0; i < 4; i++) {
        const a = t * 1.5 + (i * Math.PI) / 2;
        ctx.fillStyle = `hsla(${(i * 90 + phase) % 360}, 90%, 65%, 0.8)`;
        ctx.fillRect(x + Math.cos(a) * 24 - 2, y - 48 + Math.sin(a) * 18 - 2, 4, 4);
      }
      break;
    }
    case 'mini_bar': {
      ctx.fillStyle = '#3f2d1d'; ctx.fillRect(x - 34, y - 34, 68, 34);
      ctx.fillStyle = '#5b422a'; ctx.fillRect(x - 34, y - 40, 68, 8);
      const bc = ['#22d3ee', '#f472b6', '#a3e635', '#fbbf24'];
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = bc[i];
        ctx.fillRect(x - 24 + i * 14, y - 56, 7, 16);
      }
      ctx.fillStyle = '#fbbf24'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillText('BAR', x, y - 18);
      break;
    }
    case 'jukebox': {
      ctx.fillStyle = '#7f1d1d';
      ctx.beginPath();
      ctx.moveTo(x - 24, y); ctx.lineTo(x - 24, y - 40);
      ctx.arc(x, y - 40, 24, Math.PI, 0);
      ctx.lineTo(x + 24, y); ctx.closePath(); ctx.fill();
      const jc = `hsl(${(phase * 1.5) % 360}, 90%, 55%)`;
      ctx.strokeStyle = jc; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(x, y - 40, 19, Math.PI, 0); ctx.stroke();
      ctx.fillStyle = '#fde68a'; ctx.fillRect(x - 14, y - 34, 28, 16);
      ctx.fillStyle = '#111'; ctx.fillRect(x - 10, y - 30, 20, 3); ctx.fillRect(x - 10, y - 25, 20, 3);
      break;
    }
    case 'throne_gold': {
      ctx.fillStyle = '#b45309'; ctx.fillRect(x - 26, y - 12, 52, 12);
      ctx.fillStyle = '#f59e0b'; ctx.fillRect(x - 22, y - 58, 44, 46);
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath();
      ctx.moveTo(x - 22, y - 58); ctx.lineTo(x - 22, y - 74); ctx.lineTo(x - 10, y - 62);
      ctx.lineTo(x, y - 78); ctx.lineTo(x + 10, y - 62); ctx.lineTo(x + 22, y - 74); ctx.lineTo(x + 22, y - 58);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#dc2626'; ctx.fillRect(x - 14, y - 50, 28, 32);
      ctx.fillStyle = '#fde047';
      ctx.beginPath(); ctx.arc(x, y - 70, 3, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'wall_clock': {
      ctx.fillStyle = '#f3f4f6';
      ctx.beginPath(); ctx.arc(x, y - 44, 20, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#92400e'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(x, y - 44, 20, 0, Math.PI * 2); ctx.stroke();
      const mm = t * 0.4;
      ctx.strokeStyle = '#111827'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x, y - 44); ctx.lineTo(x + Math.cos(mm) * 13, y - 44 + Math.sin(mm) * 13); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - 44); ctx.lineTo(x + Math.cos(mm / 12) * 8, y - 44 + Math.sin(mm / 12) * 8); ctx.stroke();
      ctx.fillStyle = '#111827';
      ctx.beginPath(); ctx.arc(x, y - 44, 2, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'beanbag':
      ctx.fillStyle = '#ea580c';
      ctx.beginPath(); ctx.ellipse(x, y - 12, 30, 18, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#f97316';
      ctx.beginPath(); ctx.ellipse(x, y - 24, 22, 13, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.beginPath(); ctx.ellipse(x, y - 22, 12, 6, 0, 0, Math.PI * 2); ctx.fill();
      break;
    case 'cactus_pot':
      ctx.fillStyle = '#c2410c';
      ctx.beginPath(); ctx.moveTo(x - 14, y - 16); ctx.lineTo(x + 14, y - 16); ctx.lineTo(x + 10, y); ctx.lineTo(x - 10, y); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#16a34a';
      ctx.fillRect(x - 5, y - 52, 10, 36);
      ctx.fillRect(x - 18, y - 46, 7, 16); ctx.fillRect(x - 18, y - 39, 14, 6);
      ctx.fillRect(x + 11, y - 40, 7, 12); ctx.fillRect(x + 4, y - 34, 14, 6);
      ctx.fillStyle = '#f472b6';
      ctx.beginPath(); ctx.arc(x, y - 54, 4, 0, Math.PI * 2); ctx.fill();
      break;
    case 'standing_mirror': {
      ctx.fillStyle = '#b8860b';
      ctx.beginPath(); ctx.ellipse(x, y - 42, 18, 32, 0, 0, Math.PI * 2); ctx.fill();
      const mg = ctx.createLinearGradient(x - 12, y - 68, x + 12, y - 16);
      mg.addColorStop(0, '#dbeafe'); mg.addColorStop(0.5, '#93c5fd'); mg.addColorStop(1, '#dbeafe');
      ctx.fillStyle = mg;
      ctx.beginPath(); ctx.ellipse(x, y - 42, 13, 27, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x - 6, y - 60); ctx.lineTo(x + 3, y - 44); ctx.stroke();
      ctx.fillStyle = '#92400e'; ctx.fillRect(x - 10, y - 10, 20, 10);
      break;
    }
    case 'bar_cart': {
      ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 3;
      ctx.strokeRect(x - 28, y - 46, 56, 34);
      ctx.fillStyle = '#3f2d1d'; ctx.fillRect(x - 28, y - 48, 56, 5); ctx.fillRect(x - 28, y - 26, 56, 4);
      ['#22d3ee', '#f472b6', '#a3e635'].forEach((c2, i) => {
        ctx.fillStyle = c2; ctx.fillRect(x - 18 + i * 14, y - 62, 7, 14);
      });
      ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x - 20, y - 6, 6, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(x + 20, y - 6, 6, 0, Math.PI * 2); ctx.stroke();
      break;
    }
    case 'record_player': {
      ctx.fillStyle = '#78350f'; ctx.fillRect(x - 30, y - 16, 60, 16);
      ctx.fillStyle = '#3f2211'; ctx.fillRect(x - 30, y - 21, 60, 6);
      ctx.fillStyle = '#111';
      ctx.beginPath(); ctx.arc(x - 6, y - 30, 13, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#4b5563'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(x - 6, y - 30, 9, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = '#e11d48';
      ctx.beginPath(); ctx.arc(x - 6, y - 30, 3.5, 0, Math.PI * 2); ctx.fill();
      const ra = t * 0.9;
      ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x + 20, y - 40); ctx.lineTo(x + 2 + Math.cos(ra) * 2, y - 30 + Math.sin(ra) * 2); ctx.stroke();
      ctx.font = '11px serif'; ctx.textAlign = 'center';
      ctx.fillStyle = `rgba(253,224,71,${0.4 + Math.abs(Math.sin(t)) * 0.6})`;
      ctx.fillText('♪', x + 18, y - 48 - ((t * 4) % 14));
      break;
    }
    case 'punching_bag': {
      const sw = Math.sin(t * 1.2) * 5;
      ctx.strokeStyle = '#6b7280'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x, y - 76); ctx.lineTo(x + sw, y - 60); ctx.stroke();
      ctx.fillStyle = '#b91c1c';
      ctx.beginPath(); ctx.ellipse(x + sw, y - 60, 11, 6, 0, Math.PI, 0); ctx.fill();
      ctx.fillRect(x + sw - 11, y - 60, 22, 40);
      ctx.beginPath(); ctx.ellipse(x + sw, y - 20, 11, 6, 0, 0, Math.PI); ctx.fill();
      ctx.fillStyle = '#7f1d1d'; ctx.fillRect(x + sw - 11, y - 36, 22, 5);
      break;
    }
    case 'electric_fireplace': {
      ctx.fillStyle = '#1f2937'; ctx.fillRect(x - 34, y - 52, 68, 52);
      ctx.strokeStyle = '#4b5563'; ctx.lineWidth = 2; ctx.strokeRect(x - 34, y - 52, 68, 52);
      ctx.fillStyle = '#0a0a0a'; ctx.fillRect(x - 26, y - 44, 52, 36);
      const fh5 = 10 + Math.abs(Math.sin(t * 1.8)) * 14;
      const fgr = ctx.createLinearGradient(x, y - 10, x, y - 10 - fh5);
      fgr.addColorStop(0, 'rgba(255,90,0,0.95)'); fgr.addColorStop(1, 'rgba(255,220,90,0.4)');
      ctx.fillStyle = fgr;
      ctx.beginPath();
      ctx.moveTo(x - 18, y - 10);
      ctx.quadraticCurveTo(x - 8, y - 10 - fh5 * 0.7, x, y - 10 - fh5);
      ctx.quadraticCurveTo(x + 8, y - 10 - fh5 * 0.6, x + 18, y - 10);
      ctx.fill();
      ctx.fillStyle = `rgba(255,120,20,${0.1 + Math.abs(Math.sin(t * 1.8)) * 0.1})`;
      ctx.beginPath(); ctx.arc(x, y - 22, 40, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'indoor_fountain': {
      ctx.fillStyle = '#9ca3af';
      ctx.beginPath(); ctx.ellipse(x, y - 8, 34, 12, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath(); ctx.ellipse(x, y - 10, 27, 8, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#6b7280'; ctx.fillRect(x - 4, y - 42, 8, 32);
      ctx.fillStyle = '#9ca3af';
      ctx.beginPath(); ctx.ellipse(x, y - 42, 12, 4, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(125,211,252,0.8)'; ctx.lineWidth = 2;
      [-1, 1].forEach((d) => {
        ctx.beginPath();
        ctx.moveTo(x, y - 44);
        ctx.quadraticCurveTo(x + d * 16, y - 58, x + d * 22, y - 14);
        ctx.stroke();
      });
      for (let i = 0; i < 3; i++) {
        const dp = (t * 1.4 + i * 9) % 28;
        ctx.fillStyle = `rgba(186,230,253,${Math.max(0, 0.9 - dp / 32)})`;
        ctx.beginPath(); ctx.arc(x - 14 + i * 14, y - 44 + dp, 2, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'arcade_machine': {
      const hue = (phase * 1.4) % 360;
      ctx.fillStyle = '#14101f'; ctx.fillRect(x - 20, y - 70, 40, 70);
      ctx.strokeStyle = `hsl(${hue}, 90%, 60%)`; ctx.lineWidth = 2;
      ctx.strokeRect(x - 20, y - 70, 40, 70);
      ctx.fillStyle = `hsla(${hue}, 90%, 55%, 0.55)`;
      ctx.fillRect(x - 14, y - 62, 28, 22);
      ctx.fillStyle = '#fff';
      ctx.fillRect(x - 5 + Math.round(Math.sin(t * 1.5)) * 4, y - 56, 10, 7);
      ctx.fillStyle = '#1f2937'; ctx.fillRect(x - 16, y - 34, 32, 8);
      ctx.fillStyle = '#ef4444';
      ctx.beginPath(); ctx.arc(x - 6, y - 30, 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath(); ctx.arc(x + 6, y - 30, 3, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'pool_table': {
      ctx.fillStyle = '#5a3a1a'; ctx.fillRect(x - 44, y - 34, 88, 30);
      ctx.fillStyle = '#14532d'; ctx.fillRect(x - 38, y - 30, 76, 22);
      ctx.fillStyle = '#78350f'; ctx.fillRect(x - 42, y - 4, 8, 4); ctx.fillRect(x + 34, y - 4, 8, 4);
      [['#fff', -20, -19], ['#e11d48', 0, -15], ['#fbbf24', 14, -22], ['#3b82f6', 24, -16]].forEach(([c3, ox, oy]) => {
        ctx.fillStyle = c3;
        ctx.beginPath(); ctx.arc(x + ox, y + oy, 3, 0, Math.PI * 2); ctx.fill();
      });
      ctx.strokeStyle = '#d1a35c'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x - 30, y - 42); ctx.lineTo(x + 2, y - 18); ctx.stroke();
      break;
    }
    case 'home_cinema': {
      ctx.fillStyle = '#0a0a0a'; ctx.fillRect(x - 44, y - 66, 88, 50);
      const cg = ctx.createLinearGradient(x - 40, y - 62, x + 40, y - 20);
      cg.addColorStop(0, `hsl(${(phase * 0.6) % 360}, 70%, 25%)`);
      cg.addColorStop(1, `hsl(${(phase * 0.6 + 160) % 360}, 70%, 15%)`);
      ctx.fillStyle = cg; ctx.fillRect(x - 40, y - 62, 80, 42);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath();
      ctx.moveTo(x - 6, y - 48); ctx.lineTo(x + 8, y - 41); ctx.lineTo(x - 6, y - 34);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#374151'; ctx.fillRect(x - 44, y - 16, 88, 4);
      ctx.fillStyle = '#111'; ctx.fillRect(x - 52, y - 40, 8, 40); ctx.fillRect(x + 44, y - 40, 8, 40);
      break;
    }
    case 'grand_piano': {
      ctx.fillStyle = '#16161c';
      ctx.beginPath();
      ctx.moveTo(x - 40, y - 40); ctx.lineTo(x + 34, y - 40); ctx.lineTo(x + 10, y - 64); ctx.lineTo(x - 30, y - 60);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#0c0c10';
      ctx.beginPath();
      ctx.moveTo(x - 40, y - 40); ctx.lineTo(x + 34, y - 40); ctx.lineTo(x + 44, y - 24); ctx.lineTo(x + 20, y - 10); ctx.lineTo(x - 40, y - 16);
      ctx.closePath(); ctx.fill();
      ctx.fillRect(x - 36, y - 16, 5, 16); ctx.fillRect(x + 14, y - 10, 5, 10); ctx.fillRect(x + 36, y - 24, 5, 22);
      ctx.fillStyle = '#fff'; ctx.fillRect(x - 38, y - 22, 44, 7);
      for (let k = 0; k < 6; k++) { ctx.fillStyle = '#111'; ctx.fillRect(x - 35 + k * 7, y - 22, 3, 4); }
      ctx.font = '11px serif'; ctx.textAlign = 'center';
      ctx.fillStyle = `rgba(253,224,71,${0.4 + Math.abs(Math.sin(t * 1.3)) * 0.6})`;
      ctx.fillText('♪', x - 6 + Math.sin(t) * 5, y - 68 - ((t * 3) % 12));
      break;
    }
    case 'neon_ceiling': {
      ctx.strokeStyle = '#4b5563'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x - 20, y - 78); ctx.lineTo(x - 20, y - 64); ctx.moveTo(x + 20, y - 78); ctx.lineTo(x + 20, y - 64); ctx.stroke();
      for (let i = 0; i < 3; i++) {
        const hue2 = (phase * 1.2 + i * 120) % 360;
        ctx.strokeStyle = `hsla(${hue2}, 90%, 60%, 0.9)`;
        ctx.lineWidth = 3;
        ctx.shadowColor = `hsl(${hue2}, 90%, 60%)`;
        ctx.shadowBlur = 10;
        ctx.beginPath(); ctx.ellipse(x, y - 52 + i * 9, 34 - i * 9, 10 - i * 2.5, 0, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.shadowBlur = 0;
      break;
    }
    case 'gold_statue': {
      ctx.fillStyle = '#57534e'; ctx.fillRect(x - 20, y - 14, 40, 14);
      ctx.fillStyle = '#78716c'; ctx.fillRect(x - 24, y - 18, 48, 6);
      const gg = ctx.createLinearGradient(x - 10, y - 70, x + 10, y - 18);
      gg.addColorStop(0, '#fde047'); gg.addColorStop(0.5, '#f59e0b'); gg.addColorStop(1, '#b45309');
      ctx.fillStyle = gg;
      ctx.beginPath(); ctx.arc(x, y - 62, 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillRect(x - 8, y - 55, 16, 24);
      ctx.fillRect(x - 14, y - 52, 6, 16); ctx.fillRect(x + 8, y - 52, 6, 16);
      ctx.fillRect(x - 7, y - 31, 5, 13); ctx.fillRect(x + 2, y - 31, 5, 13);
      const sp2 = Math.abs(Math.sin(t * 2));
      ctx.fillStyle = `rgba(255,255,220,${sp2})`;
      ctx.beginPath(); ctx.arc(x + 9, y - 68, 2 + sp2 * 1.5, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'rug_rainbow': {
      const rbc = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];
      rbc.forEach((c, i) => {
        ctx.fillStyle = c;
        ctx.beginPath(); ctx.ellipse(x, y, 55 - i * 8, 20 - i * 3, 0, 0, Math.PI * 2); ctx.fill();
      });
      break;
    }
    case 'plant_bonsai':
      ctx.fillStyle = '#7f1d1d'; ctx.fillRect(x - 14, y - 10, 28, 10);
      ctx.strokeStyle = '#6b4423'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(x, y - 10); ctx.quadraticCurveTo(x + 8, y - 22, x - 4, y - 32); ctx.stroke();
      ctx.fillStyle = '#15803d';
      [[-10, -34], [4, -40], [12, -30]].forEach(([ox, oy]) => {
        ctx.beginPath(); ctx.ellipse(x + ox, y + oy, 10, 6, 0, 0, Math.PI * 2); ctx.fill();
      });
      break;
    case 'floor_pouf':
      ctx.fillStyle = '#be185d';
      ctx.beginPath(); ctx.ellipse(x, y - 8, 20, 12, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#9d174d';
      ctx.beginPath(); ctx.ellipse(x, y - 2, 20, 8, 0, 0, Math.PI); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1.5;
      [[-10, 0], [0, -2], [10, 0]].forEach(([ox]) => {
        ctx.beginPath(); ctx.moveTo(x + ox, y - 16); ctx.lineTo(x, y - 8); ctx.stroke();
      });
      break;
    case 'wall_art':
      ctx.fillStyle = '#c9a44a'; ctx.fillRect(x - 26, y - 66, 52, 40);
      ctx.fillStyle = '#0f172a'; ctx.fillRect(x - 22, y - 62, 44, 32);
      ctx.fillStyle = '#f472b6'; ctx.beginPath(); ctx.arc(x - 8, y - 48, 8, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#38bdf8'; ctx.fillRect(x + 2, y - 58, 14, 22);
      ctx.strokeStyle = '#fde047'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x - 18, y - 36); ctx.lineTo(x + 16, y - 54); ctx.stroke();
      break;
    case 'string_lights':
      ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x - 44, y - 60); ctx.quadraticCurveTo(x, y - 44, x + 44, y - 60); ctx.stroke();
      for (let i = 0; i < 7; i++) {
        const lx = x - 39 + i * 13;
        const ly = y - 58 + Math.sin((i / 6) * Math.PI) * 12;
        const on = (Math.floor(phase / 18) + i) % 3 !== 0;
        ctx.fillStyle = on ? ['#f472b6', '#fde047', '#38bdf8', '#4ade80'][i % 4] : 'rgba(120,120,130,0.5)';
        ctx.beginPath(); ctx.arc(lx, ly, 3.4, 0, Math.PI * 2); ctx.fill();
      }
      break;
    case 'side_table':
      ctx.fillStyle = '#e5e7eb'; ctx.fillRect(x - 18, y - 26, 36, 7);
      ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x - 12, y - 22); ctx.lineTo(x + 8, y - 24); ctx.stroke();
      ctx.fillStyle = '#c9a44a';
      ctx.fillRect(x - 14, y - 19, 4, 19); ctx.fillRect(x + 10, y - 19, 4, 19);
      break;
    case 'coat_rack':
      ctx.fillStyle = '#5b3a1e'; ctx.fillRect(x - 2, y - 62, 4, 62);
      ctx.fillRect(x - 14, y - 2, 28, 3);
      ctx.strokeStyle = '#5b3a1e'; ctx.lineWidth = 3;
      [[-12, -56], [12, -56], [-10, -46], [10, -46]].forEach(([ox, oy]) => {
        ctx.beginPath(); ctx.moveTo(x, y + oy + 4); ctx.lineTo(x + ox, y + oy); ctx.stroke();
      });
      ctx.fillStyle = '#7c3aed'; ctx.fillRect(x + 6, y - 56, 12, 20);
      ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(x - 12, y - 54, 5, 0, Math.PI * 2); ctx.fill();
      break;
    case 'floor_candles':
      [[-14, 0, 16], [0, 0, 24], [14, 0, 12]].forEach(([ox, oy, h], i) => {
        ctx.fillStyle = '#f5f0e6'; ctx.fillRect(x + ox - 4, y - h, 8, h);
        const fl = 1 + Math.sin(t * 6 + i * 2) * 0.3;
        ctx.fillStyle = '#fb923c';
        ctx.beginPath(); ctx.ellipse(x + ox, y - h - 5 * fl, 2.4, 5 * fl, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(251,146,60,0.12)';
        ctx.beginPath(); ctx.arc(x + ox, y - h - 4, 11, 0, Math.PI * 2); ctx.fill();
      });
      break;
    case 'skate_rack':
      ctx.fillStyle = '#374151'; ctx.fillRect(x - 26, y - 46, 52, 4);
      [['#f472b6', -18], ['#4ade80', 0], ['#38bdf8', 18]].forEach(([c, ox]) => {
        ctx.save();
        ctx.translate(x + ox, y - 24);
        ctx.rotate(-0.12);
        ctx.fillStyle = c;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(-5, -18, 10, 38, 5); else ctx.rect(-5, -18, 10, 38);
        ctx.fill();
        ctx.fillStyle = '#111';
        ctx.fillRect(-6, -10, 2.5, 5); ctx.fillRect(3.5, -10, 2.5, 5);
        ctx.fillRect(-6, 8, 2.5, 5); ctx.fillRect(3.5, 8, 2.5, 5);
        ctx.restore();
      });
      break;
    case 'neon_turtle': {
      const gl = 0.6 + Math.abs(Math.sin(t * 2)) * 0.4;
      ctx.save();
      ctx.shadowColor = '#4ade80';
      ctx.shadowBlur = 14 * gl;
      ctx.strokeStyle = `rgba(74,222,128,${gl})`;
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.ellipse(x, y - 40, 20, 14, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(x + 24, y - 44, 6, 0, Math.PI * 2); ctx.stroke();
      [[-16, 10], [16, 10], [-18, -10], [18, -10]].forEach(([ox, oy]) => {
        ctx.beginPath(); ctx.moveTo(x + ox * 0.8, y - 40 + oy * 0.8); ctx.lineTo(x + ox * 1.4, y - 40 + oy * 1.4); ctx.stroke();
      });
      ctx.restore();
      break;
    }
    case 'surfboard':
      ctx.save();
      ctx.translate(x, y - 30);
      ctx.rotate(0.16);
      const sg = ctx.createLinearGradient(0, -34, 0, 34);
      sg.addColorStop(0, '#38bdf8'); sg.addColorStop(1, '#fde047');
      ctx.fillStyle = sg;
      ctx.beginPath(); ctx.ellipse(0, 0, 11, 34, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.3)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(0, -30); ctx.lineTo(0, 30); ctx.stroke();
      ctx.restore();
      break;
    case 'drum_kit':
      ctx.fillStyle = '#b91c1c';
      ctx.beginPath(); ctx.ellipse(x, y - 14, 18, 14, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#f5f0e6';
      ctx.beginPath(); ctx.ellipse(x, y - 20, 18, 7, 0, 0, Math.PI * 2); ctx.fill();
      [[-24, -6, 10], [24, -6, 10]].forEach(([ox, oy, r]) => {
        ctx.fillStyle = '#7f1d1d';
        ctx.beginPath(); ctx.ellipse(x + ox, y + oy - 14, r, 8, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#f5f0e6';
        ctx.beginPath(); ctx.ellipse(x + ox, y + oy - 18, r, 4, 0, 0, Math.PI * 2); ctx.fill();
      });
      ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x - 34, y - 52); ctx.lineTo(x - 28, y - 34); ctx.stroke();
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath(); ctx.ellipse(x - 36, y - 54, 10, 3.5, -0.3, 0, Math.PI * 2); ctx.fill();
      break;
    case 'hammock':
      ctx.fillStyle = '#5b3a1e';
      ctx.fillRect(x - 44, y - 46, 5, 46); ctx.fillRect(x + 39, y - 46, 5, 46);
      ctx.strokeStyle = '#d9bd8a'; ctx.lineWidth = 2;
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(x - 40, y - 40);
        ctx.quadraticCurveTo(x, y - 12 + i * 2.5, x + 40, y - 40);
        ctx.stroke();
      }
      ctx.strokeStyle = 'rgba(217,189,138,0.6)';
      for (let i = 0; i < 5; i++) {
        const hx = x - 28 + i * 14;
        ctx.beginPath(); ctx.moveTo(hx, y - 32 + Math.abs(i - 2) * -1 + 6); ctx.lineTo(hx, y - 18); ctx.stroke();
      }
      break;
    case 'vinyl_wall':
      ctx.fillStyle = '#1f2430'; ctx.fillRect(x - 34, y - 66, 68, 44);
      for (let r2 = 0; r2 < 2; r2++) {
        for (let c2 = 0; c2 < 3; c2++) {
          const vx = x - 22 + c2 * 22;
          const vy = y - 55 + r2 * 22;
          ctx.fillStyle = '#0b0d12';
          ctx.beginPath(); ctx.arc(vx, vy, 8.5, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = ['#f472b6', '#38bdf8', '#fde047', '#4ade80', '#a78bfa', '#fb923c'][r2 * 3 + c2];
          ctx.beginPath(); ctx.arc(vx, vy, 3, 0, Math.PI * 2); ctx.fill();
        }
      }
      break;
    case 'slot_machine': {
      ctx.fillStyle = '#7f1d1d'; ctx.fillRect(x - 20, y - 58, 40, 58);
      ctx.fillStyle = '#fbbf24'; ctx.fillRect(x - 20, y - 58, 40, 8);
      ctx.fillStyle = '#f5f0e6'; ctx.fillRect(x - 15, y - 44, 30, 16);
      const syms = ['7', '🍒', '★'];
      ctx.font = '9px monospace'; ctx.textAlign = 'center'; ctx.fillStyle = '#b91c1c';
      for (let i = 0; i < 3; i++) {
        const sym = syms[Math.floor(phase / 10 + i) % 3];
        ctx.fillText(sym, x - 9 + i * 9, y - 33);
      }
      ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(x + 20, y - 40); ctx.lineTo(x + 28, y - 52); ctx.stroke();
      ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(x + 28, y - 54, 4, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#111'; ctx.fillRect(x - 10, y - 20, 20, 8);
      break;
    }
    case 'dj_booth_mini': {
      ctx.fillStyle = '#1f2430'; ctx.fillRect(x - 36, y - 34, 72, 34);
      const hue2 = (phase * 3) % 360;
      ctx.fillStyle = `hsl(${hue2}, 90%, 55%)`;
      ctx.fillRect(x - 36, y - 8, 72, 4);
      [[-18], [18]].forEach(([ox]) => {
        ctx.fillStyle = '#0b0d12';
        ctx.beginPath(); ctx.arc(x + ox, y - 22, 10, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#374151';
        ctx.beginPath(); ctx.arc(x + ox, y - 22, 3.4, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#4b5563'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x + ox, y - 22); ctx.lineTo(x + ox + 8 * Math.cos(t * 5 + ox), y - 22 + 8 * Math.sin(t * 5 + ox)); ctx.stroke();
      });
      ctx.fillStyle = '#111'; ctx.fillRect(x - 6, y - 26, 12, 10);
      break;
    }
    case 'claw_machine':
      ctx.fillStyle = '#be185d'; ctx.fillRect(x - 22, y - 64, 44, 64);
      ctx.fillStyle = 'rgba(147,197,253,0.35)'; ctx.fillRect(x - 17, y - 56, 34, 34);
      ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x + Math.sin(t * 2) * 8, y - 56); ctx.lineTo(x + Math.sin(t * 2) * 8, y - 44); ctx.stroke();
      ctx.fillStyle = '#d1d5db';
      ctx.fillText('', 0, 0);
      ctx.beginPath(); ctx.arc(x + Math.sin(t * 2) * 8, y - 42, 3, 0, Math.PI); ctx.fill();
      [['#4ade80', -8], ['#fde047', 2], ['#f472b6', 10]].forEach(([c, ox]) => {
        ctx.fillStyle = c;
        ctx.beginPath(); ctx.arc(x + ox, y - 27, 5, 0, Math.PI * 2); ctx.fill();
      });
      ctx.fillStyle = '#111'; ctx.fillRect(x - 16, y - 16, 32, 10);
      break;
    case 'vending_machine':
      ctx.fillStyle = '#1d4ed8'; ctx.fillRect(x - 20, y - 62, 40, 62);
      ctx.fillStyle = 'rgba(191,219,254,0.3)'; ctx.fillRect(x - 15, y - 56, 22, 40);
      for (let r2 = 0; r2 < 3; r2++) {
        for (let c2 = 0; c2 < 2; c2++) {
          ctx.fillStyle = ['#dc2626', '#f97316', '#4ade80', '#fde047', '#a78bfa', '#38bdf8'][r2 * 2 + c2];
          ctx.fillRect(x - 12 + c2 * 9, y - 52 + r2 * 13, 6, 9);
        }
      }
      ctx.fillStyle = '#111'; ctx.fillRect(x + 10, y - 50, 7, 20);
      ctx.fillStyle = '#0b0d12'; ctx.fillRect(x - 14, y - 12, 28, 7);
      break;
    case 'hot_tub_mini': {
      ctx.fillStyle = '#5b3a1e';
      ctx.beginPath(); ctx.ellipse(x, y - 10, 34, 18, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath(); ctx.ellipse(x, y - 12, 27, 12, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      for (let i = 0; i < 4; i++) {
        const bx = x - 16 + i * 10 + Math.sin(t * 3 + i) * 3;
        const by = y - 12 - ((t * 9 + i * 6) % 10);
        ctx.beginPath(); ctx.arc(bx, by, 2, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'terrarium':
      ctx.fillStyle = '#374151'; ctx.fillRect(x - 26, y - 8, 52, 8);
      ctx.fillStyle = 'rgba(147,197,253,0.25)'; ctx.fillRect(x - 24, y - 38, 48, 30);
      ctx.strokeStyle = '#6b7280'; ctx.strokeRect(x - 24, y - 38, 48, 30);
      ctx.fillStyle = '#d9bd8a'; ctx.fillRect(x - 22, y - 14, 44, 5);
      // little turtle waddling
      const tx2 = x - 12 + ((phase * 0.2) % 24);
      ctx.fillStyle = '#166534';
      ctx.beginPath(); ctx.ellipse(tx2, y - 17, 6, 4, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#4ade80';
      ctx.beginPath(); ctx.arc(tx2 + 6, y - 18, 2.2, 0, Math.PI * 2); ctx.fill();
      break;
    case 'pinball': {
      ctx.save();
      ctx.fillStyle = '#4c1d95';
      ctx.beginPath();
      ctx.moveTo(x - 20, y); ctx.lineTo(x - 16, y - 40); ctx.lineTo(x + 16, y - 40); ctx.lineTo(x + 20, y);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#1f1147'; ctx.fillRect(x - 16, y - 62, 32, 22);
      const bl = (Math.floor(phase / 12) % 2) === 0;
      ctx.fillStyle = bl ? '#fde047' : '#f472b6';
      ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillText('PINBALL', x, y - 49);
      ctx.fillStyle = '#e5e7eb';
      ctx.beginPath(); ctx.arc(x - 6 + ((phase * 0.6) % 12), y - 26, 2.4, 0, Math.PI * 2); ctx.fill();
      [['#dc2626', -8, -32], ['#38bdf8', 8, -32], ['#4ade80', 0, -20]].forEach(([c, ox, oy]) => {
        ctx.fillStyle = c;
        ctx.beginPath(); ctx.arc(x + ox, y + oy, 3, 0, Math.PI * 2); ctx.fill();
      });
      ctx.restore();
      break;
    }
    case 'ice_sculpture': {
      ctx.save();
      ctx.fillStyle = '#d1d5db'; ctx.fillRect(x - 18, y - 8, 36, 8);
      const ig = ctx.createLinearGradient(x, y - 60, x, y - 8);
      ig.addColorStop(0, 'rgba(191,219,254,0.9)'); ig.addColorStop(1, 'rgba(147,197,253,0.55)');
      ctx.fillStyle = ig;
      ctx.beginPath();
      ctx.moveTo(x, y - 60); ctx.lineTo(x + 14, y - 34); ctx.lineTo(x + 8, y - 8);
      ctx.lineTo(x - 8, y - 8); ctx.lineTo(x - 14, y - 34);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.8)'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x - 4, y - 50); ctx.lineTo(x - 8, y - 20); ctx.stroke();
      const sp = (phase % 40) / 40;
      ctx.fillStyle = `rgba(255,255,255,${0.9 - sp})`;
      ctx.beginPath(); ctx.arc(x + 10 - sp * 6, y - 52 + sp * 10, 1.6, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      break;
    }
    case 'koi_pond': {
      ctx.fillStyle = '#4b5563';
      ctx.beginPath(); ctx.ellipse(x, y - 6, 46, 22, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#155e75';
      ctx.beginPath(); ctx.ellipse(x, y - 7, 39, 17, 0, 0, Math.PI * 2); ctx.fill();
      const ka = t * 1.2;
      [['#fb923c', 0], ['#f5f0e6', Math.PI]].forEach(([c, off]) => {
        const kx = x + Math.cos(ka + off) * 22;
        const ky = y - 7 + Math.sin(ka + off) * 9;
        ctx.fillStyle = c;
        ctx.beginPath(); ctx.ellipse(kx, ky, 7, 3, ka + off + Math.PI / 2, 0, Math.PI * 2); ctx.fill();
      });
      ctx.fillStyle = '#4ade80';
      ctx.beginPath(); ctx.ellipse(x - 20, y - 14, 7, 3.4, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(x + 24, y - 2, 6, 3, 0, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'crystal_chandelier': {
      ctx.strokeStyle = '#c9a44a'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x, y - 78); ctx.lineTo(x, y - 64); ctx.stroke();
      ctx.fillStyle = '#c9a44a';
      ctx.beginPath(); ctx.ellipse(x, y - 60, 24, 6, 0, 0, Math.PI * 2); ctx.fill();
      const gl2 = 0.5 + Math.abs(Math.sin(t * 2.4)) * 0.5;
      for (let i = 0; i < 7; i++) {
        const cx2 = x - 21 + i * 7;
        const len = 8 + Math.abs(3 - i) * -1 + 12;
        ctx.strokeStyle = `rgba(224,242,254,${0.5 + gl2 * 0.4})`;
        ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.moveTo(cx2, y - 58); ctx.lineTo(cx2, y - 58 + len); ctx.stroke();
        ctx.fillStyle = `rgba(186,230,253,${gl2})`;
        ctx.beginPath();
        ctx.moveTo(cx2, y - 58 + len); ctx.lineTo(cx2 - 2.4, y - 54 + len); ctx.lineTo(cx2, y - 50 + len); ctx.lineTo(cx2 + 2.4, y - 54 + len);
        ctx.closePath(); ctx.fill();
      }
      ctx.fillStyle = `rgba(253,230,138,${0.12 * gl2})`;
      ctx.beginPath(); ctx.arc(x, y - 48, 34, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'money_pile': {
      ctx.fillStyle = '#15803d';
      ctx.beginPath(); ctx.ellipse(x, y - 8, 32, 14, 0, 0, Math.PI * 2); ctx.fill();
      for (let i = 0; i < 7; i++) {
        const mx = x - 22 + (i * 7.3) % 44;
        const my2 = y - 12 - (i % 3) * 7;
        ctx.fillStyle = i % 2 ? '#16a34a' : '#22c55e';
        ctx.save();
        ctx.translate(mx, my2);
        ctx.rotate((i - 3) * 0.12);
        ctx.fillRect(-9, -4, 18, 8);
        ctx.strokeStyle = '#065f46'; ctx.lineWidth = 1;
        ctx.strokeRect(-9, -4, 18, 8);
        ctx.fillStyle = '#065f46';
        ctx.font = 'bold 7px monospace'; ctx.textAlign = 'center';
        ctx.fillText('$', 0, 2.6);
        ctx.restore();
      }
      const gs = (phase % 50) / 50;
      ctx.fillStyle = `rgba(253,230,138,${1 - gs})`;
      ctx.font = `${8 + gs * 6}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('✨', x + 18 - gs * 8, y - 34 - gs * 10);
      break;
    }
    default:
      ctx.fillStyle = '#6b7280'; ctx.fillRect(x - 12, y - 12, 24, 12);
  }
  ctx.restore();
}

// ==================== MAIN COMPONENT ====================
export default function ApartmentComplex({
  user, avatar, vibecoins, setVibecoins, authFetch, API_URL, socketRef, isMobile, players = {}, playersLiveRef, currentEmote, emoteEndTime, onExit, onGoCity, serverRoomRef, chatMessagesRef,
}) {
  const canvasRef = useRef(null);
  // Portrait (9:16) screens get a taller canvas so the complex fills the screen
  const [H] = useState(() => {
    const vw = window.innerWidth;
    const vh = Math.max(320, window.innerHeight - 64);
    const a = vw / vh;
    if (a >= W / BASE_H) return BASE_H;
    return Math.min(1400, Math.round(W / Math.max(0.5, a)));
  });
  const offY = Math.max(0, Math.round((H - BASE_H) * 0.42));
  const [mode, setMode] = useState('hallway');
  const [rooms, setRooms] = useState([]);
  const [penthouse, setPenthouse] = useState(null);
  const [myApt, setMyApt] = useState(null);
  const [ownedItems, setOwnedItems] = useState([]);
  const [interior, setInterior] = useState(null); // {roomNumber, isOwner, isPenthouse, subRoom, furniture, ownerName, expiresAt}
  const [rentPrompt, setRentPrompt] = useState(null);
  const [claimPrompt, setClaimPrompt] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [placingItem, setPlacingItem] = useState(null);
  const isStaff = ['owner', 'admin', 'overseer'].includes(user?.role);

  const posRef = useRef({ x: 400, y: 150 });
  const targetRef = useRef(null);
  const dirRef = useRef('down');
  const walkingRef = useRef(false);
  const walkFrameRef = useRef(0);
  const camRef = useRef(0);
  const soakingRef = useRef(false);
  const soakEnterRef = useRef(0);
  const lastTouchRef = useRef(0);
  const phaseRef = useRef(0);
  const seqRef = useRef(0);
  const lastSendRef = useRef(0);
  const modeRef = useRef('hallway');
  const interiorRef = useRef(null);
  const editRef = useRef({ editMode: false, placingItem: null });
  const keysRef = useRef({});
  const keyWalkRef = useRef(false);
  const knockWaitRef = useRef(null);
  const myEmoteRef = useRef(null);
  useEffect(() => {
    myEmoteRef.current = currentEmote ? { id: currentEmote, until: emoteEndTime || 0 } : null;
  }, [currentEmote, emoteEndTime]);
  const localPlayersRef = useRef(players);
  useEffect(() => { localPlayersRef.current = players; }, [players]);
  // live ref from Nightclub sees every player_moved packet (state prop only updates on join/leave)
  const playersRef = playersLiveRef || localPlayersRef;
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { interiorRef.current = interior; }, [interior]);
  useEffect(() => { editRef.current = { editMode, placingItem }; }, [editMode, placingItem]);

  // WASD / arrow keys — desktop movement parity with the club
  useEffect(() => {
    const KEYMAP = { w: 'up', arrowup: 'up', s: 'down', arrowdown: 'down', a: 'left', arrowleft: 'left', d: 'right', arrowright: 'right' };
    const isTyping = (e) => {
      const t = e.target;
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    };
    const down = (e) => {
      const k = KEYMAP[e.key?.toLowerCase()];
      if (!k || isTyping(e)) return;
      if (e.key.startsWith('Arrow')) e.preventDefault();
      keysRef.current[k] = true;
    };
    const up = (e) => {
      const k = KEYMAP[e.key?.toLowerCase()];
      if (k) keysRef.current[k] = false;
    };
    const clear = () => { keysRef.current = {}; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
    };
  }, []);

  const socketRoomId = useCallback((m, itr) => (
    m === 'interior' && itr ? `apt_${itr.roomNumber}_${itr.subRoom}` : 'apartments'
  ), []);

  const emitRoom = useCallback((roomId) => {
    if (serverRoomRef) serverRoomRef.current = roomId;
    socketRef?.current?.emit('change_room', { room: roomId, user_id: user?.user_id });
  }, [socketRef, user, serverRoomRef]);

  const loadRooms = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments`);
      const data = await res?.json();
      if (data?.rooms) {
        setRooms(data.rooms);
        setPenthouse(data.penthouse || null);
      }
    } catch (e) { /* noop */ }
  }, [authFetch, API_URL]);

  const loadMine = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments/my`);
      const data = await res?.json();
      if (data) {
        setMyApt(data.apartment);
        setOwnedItems(data.owned_items || []);
      }
    } catch (e) { /* noop */ }
  }, [authFetch, API_URL]);

  useEffect(() => {
    ensureSpriteLoaded(resolveGender(avatar?.gender));
    loadRooms();
    loadMine();
  }, [loadRooms, loadMine, avatar]);

  // Knock result (visitor side)
  useEffect(() => {
    const onResult = async (data) => {
      if (data.property === 'house') return;
      if (knockWaitRef.current) { clearTimeout(knockWaitRef.current); knockWaitRef.current = null; }
      if (data.accepted) {
        toast.success(`${data.owner_name} let you in!`);
        try {
          const res = await authFetch(`${API_URL}/api/apartments/${data.room_number}`);
          const apt = await res?.json();
          if (apt && apt.room_number !== undefined && apt.owner_user_id) {
            enterInterior(apt, false);
          }
        } catch (e) { /* noop */ }
      } else {
        toast.error('They didn\'t let you in this time.');
      }
    };
    // Re-attach whenever the socket instance is replaced (reconnect/recreate)
    let bound = null;
    const attach = () => {
      const s = socketRef?.current;
      if (s && s !== bound) {
        if (bound) bound.off('apartment_knock_result', onResult);
        s.on('apartment_knock_result', onResult);
        bound = s;
      }
    };
    attach();
    const iv = setInterval(attach, 2000);
    return () => { clearInterval(iv); if (bound) bound.off('apartment_knock_result', onResult); };
    // eslint-disable-next-line
  }, []);

  const enterInterior = (apt, isOwner) => {
    const isPent = apt.room_number === 0;
    soakingRef.current = false;
    setInterior({
      roomNumber: apt.room_number,
      isOwner,
      isPenthouse: isPent,
      subRoom: isPent ? 'penthouse_main' : 'bedroom',
      furniture: apt.furniture || [],
      ownerName: apt.owner_username,
      expiresAt: apt.expires_at,
    });
    setMode('interior');
    posRef.current = { x: 400, y: 400 };
    targetRef.current = null;
    emitRoom(`apt_${apt.room_number}_${isPent ? 'penthouse_main' : 'bedroom'}`);
  };

  const exitToHallway = () => {
    const itr = interiorRef.current;
    setMode('hallway');
    setInterior(null);
    setEditMode(false);
    setPlacingItem(null);
    soakingRef.current = false;
    // step back out right at your own front door
    if (itr && !itr.isPenthouse && itr.roomNumber) {
      const d = doorPos(itr.roomNumber);
      posRef.current = { x: d.left ? 168 : 632, y: d.y + 8 };
    } else {
      posRef.current = { x: 400, y: 200 };
    }
    targetRef.current = null;
    camRef.current = Math.max(0, Math.min(WORLD_H - H, posRef.current.y - H * 0.45));
    socketRef?.current?.emit('player_move', {
      x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: 'down',
    });
    emitRoom('apartments');
    loadRooms();
  };

  const goSubRoom = (sub) => {
    setInterior((prev) => {
      if (!prev) return prev;
      emitRoom(`apt_${prev.roomNumber}_${sub}`);
      return { ...prev, subRoom: sub };
    });
    soakingRef.current = false;
    posRef.current = { x: 400, y: 400 };
    targetRef.current = null;
  };

  const handleRent = async (roomNumber) => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments/rent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_number: roomNumber }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        setMyApt(data.apartment);
        toast.success(data.message);
        setRentPrompt(null);
        loadRooms();
        enterInterior(data.apartment, true);
      } else {
        toast.error(data?.detail || 'Rent failed');
      }
    } catch (e) { toast.error('Rent failed'); }
  };

  const handleRenew = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments/renew`, { method: 'POST' });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        setInterior((p) => p ? { ...p, expiresAt: data.expires_at } : p);
        setMyApt((p) => p ? { ...p, expires_at: data.expires_at } : p);
        toast.success(data.message);
      } else toast.error(data?.detail || 'Renew failed');
    } catch (e) { toast.error('Renew failed'); }
  };

  const handleClaim = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments/penthouse/claim`, { method: 'POST' });
      const data = await res?.json();
      if (res?.ok) {
        toast.success(data.message);
        setClaimPrompt(false);
        loadRooms();
        enterInterior(data.apartment, true);
      } else toast.error(data?.detail || 'Claim failed');
    } catch (e) { toast.error('Claim failed'); }
  };

  const handleKnock = async (roomNumber) => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments/${roomNumber}/knock`, { method: 'POST' });
      const data = await res?.json();
      if (res?.ok) {
        toast.info(data.message);
        if (knockWaitRef.current) clearTimeout(knockWaitRef.current);
        knockWaitRef.current = setTimeout(() => {
          toast.info('No answer at the door... 🚪 Maybe try again in a bit.');
          knockWaitRef.current = null;
        }, 25000);
      } else toast.error(data?.detail || 'Knock failed');
    } catch (e) { toast.error('Knock failed'); }
  };

  const handleBuyFurniture = async (item) => {
    try {
      const res = await authFetch(`${API_URL}/api/apartments/furniture/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.id }),
      });
      const data = await res?.json();
      if (res?.ok) {
        setVibecoins(data.new_balance);
        setOwnedItems((p) => [...p, item.id]);
        toast.success(data.message);
      } else toast.error(data?.detail || 'Purchase failed');
    } catch (e) { toast.error('Purchase failed'); }
  };

  const saveLayout = async (furniture) => {
    try {
      await authFetch(`${API_URL}/api/apartments/layout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ furniture, penthouse: !!interiorRef.current?.isPenthouse }),
      });
    } catch (e) { /* noop */ }
  };

  // ==================== INPUT ====================
  const canvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * (W / rect.width);
    const cy = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * (H / rect.height);
    return { cx, cy };
  };

  const handleClick = (e) => {
    const { cx, cy } = canvasPoint(e);
    if (modeRef.current === 'hallway') {
      const wy = cy + camRef.current;
      // city exit door at world top (left of elevator)
      if (wy < 175 && cx > 100 && cx < 300) {
        onGoCity?.();
        return;
      }
      // penthouse door at world top (right of elevator) — live check so it never
      // wrongly asks the owner to re-claim while data is still loading
      if (wy < 175 && cx > 495 && cx < 700) {
        authFetch(`${API_URL}/api/apartments/0`).then((r) => (r?.ok ? r.json() : null)).then((apt) => {
          if (apt && apt.owner_user_id === user?.user_id) enterInterior(apt, true);
          else if (apt && apt.owner_user_id) handleKnock(0);
          else if (isStaff) setClaimPrompt(true);
          else toast.info('The Penthouse is reserved for the club owner 👑');
        }).catch(() => {});
        return;
      }
      // elevator exit at top
      if (wy < 150 && cx > 330 && cx < 470) { onExit?.(); return; }
      // door hit test
      for (let n = 1; n <= 30; n++) {
        const d = doorPos(n);
        if (Math.abs(cx - d.x) < 42 && Math.abs(wy - (d.y - 40)) < 62) {
          const info = rooms.find((r) => r.room_number === n);
          if (info?.occupied && info.owner_user_id === user?.user_id) {
            authFetch(`${API_URL}/api/apartments/${n}`).then((r) => r?.json()).then((apt) => {
              if (apt && apt.room_number !== undefined) enterInterior(apt, true);
            });
          } else if (info?.occupied) {
            handleKnock(n);
          } else {
            setRentPrompt(n);
          }
          return;
        }
      }
      targetRef.current = {
        x: Math.max(CORRIDOR.minX, Math.min(CORRIDOR.maxX, cx)),
        y: Math.max(140, Math.min(WORLD_H - 40, wy)),
      };
    } else {
      const itr = interiorRef.current;
      const cy2 = cy - offY; // scene coords (portrait canvases are vertically centered)
      const { editMode: em, placingItem: pi } = editRef.current;
      if (itr && em && itr.isOwner) {
        // remove a placed item if clicked
        const placed = (itr.furniture || []).filter((f) => f.sub_room === itr.subRoom);
        for (let i = placed.length - 1; i >= 0; i--) {
          const f = placed[i];
          if (Math.abs(cx - f.x) < 40 && Math.abs(cy2 - f.y) < 45) {
            const next = itr.furniture.filter((ff) => ff !== f);
            setInterior({ ...itr, furniture: next });
            saveLayout(next);
            toast.info('Item returned to inventory');
            return;
          }
        }
        if (pi) {
          const next = [...(itr.furniture || []), {
            item_id: pi,
            sub_room: itr.subRoom,
            x: Math.round(Math.max(60, Math.min(740, cx))),
            y: Math.round(Math.max(160, Math.min(470, cy2))),
          }];
          setInterior({ ...itr, furniture: next });
          setPlacingItem(null);
          saveLayout(next);
          return;
        }
        return;
      }
      // hot tub soak (penthouse main floor) — pick a cozy seat
      if (itr?.subRoom === 'penthouse_main' && Math.abs(cx - 185) < 98 && Math.abs(cy2 - 415) < 45) {
        const soakedLongEnough = Date.now() - soakEnterRef.current > 800;
        if (soakingRef.current && soakedLongEnough && Math.abs(cx - posRef.current.x) < 30 && Math.abs(cy2 - posRef.current.y) < 34) {
          soakingRef.current = false;
          targetRef.current = { x: 185, y: 474 };
          return;
        }
        if (soakingRef.current && !soakedLongEnough) return;
        const roomId = `apt_${itr.roomNumber}_${itr.subRoom}`;
        const others = Object.values(playersRef.current || {}).filter((p) => (p.room || 'main') === roomId && p.x);
        const free = TUB_SEATS.filter((s) => !others.some((o) => Math.abs(o.x - s.x) < 26 && Math.abs(o.y - s.y) < 26));
        const pool = free.length ? free : TUB_SEATS;
        let seat = pool[0];
        let best = Infinity;
        pool.forEach((s) => {
          const d = (s.x - cx) ** 2 + (s.y - cy2) ** 2;
          if (d < best) { best = d; seat = s; }
        });
        const firstDip = !soakingRef.current;
        soakingRef.current = true;
        soakEnterRef.current = Date.now();
        posRef.current = { x: seat.x, y: seat.y };
        targetRef.current = null;
        dirRef.current = 'down';
        socketRef?.current?.emit('player_move', { seq: ++seqRef.current, x: seat.x, y: seat.y, is_walking: false, direction: 'down' });
        if (firstDip) toast.success("Ahhh... that's the good stuff 🛁♨️");
        return;
      }
      // penthouse staircase (right side)
      if (itr?.subRoom === 'penthouse_main' && cx > 630 && cx < 785 && cy2 > 140 && cy2 < 330) {
        goSubRoom('penthouse_upper');
        return;
      }
      if (itr?.subRoom === 'penthouse_upper' && cx > 630 && cx < 785 && cy2 > 140 && cy2 < 330) {
        goSubRoom('penthouse_main');
        return;
      }
      // bathroom door hit (bedroom only)
      if (itr?.subRoom === 'bedroom' && cx > 52 && cx < 128 && cy2 > 160 && cy2 < 300) {
        goSubRoom('bathroom');
        return;
      }
      if (itr?.subRoom === 'bathroom' && cx > 52 && cx < 128 && cy2 > 160 && cy2 < 300) {
        goSubRoom('bedroom');
        return;
      }
      // exit door mat (entry room, bottom center) — walk out the front door
      const entrySub = itr?.isPenthouse ? 'penthouse_main' : 'bedroom';
      if (itr && itr.subRoom === entrySub && cx > 325 && cx < 475 && cy2 > 440) {
        if (soakingRef.current && Date.now() - soakEnterRef.current < 800) return;
        soakingRef.current = false;
        targetRef.current = { x: 400, y: 472 };
        return;
      }
      // floor walk (a deliberate tap outside the tub hops you out)
      if (soakingRef.current && Date.now() - soakEnterRef.current < 800) return;
      soakingRef.current = false;
      targetRef.current = {
        x: Math.max(70, Math.min(730, cx)),
        y: Math.max(300, Math.min(480, cy2)),
      };
    }
  };

  // ==================== RENDER LOOP ====================
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;

    const drawEmoteBubble = (x, y, emoteId) => {
      const icon = EMOTE_ICONS[emoteId] || '✨';
      const bob2 = Math.sin(phaseRef.current * 0.15) * 3;
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,0.94)';
      ctx.beginPath();
      ctx.arc(x, y - 80 + bob2, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x - 5, y - 68 + bob2);
      ctx.lineTo(x + 5, y - 68 + bob2);
      ctx.lineTo(x, y - 59 + bob2);
      ctx.closePath();
      ctx.fill();
      ctx.font = '17px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(icon, x, y - 74 + bob2);
      ctx.restore();
    };

    const chatFor = (pid) => {
      const msgs = chatMessagesRef?.current || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        const ttl = Math.min(9000, 3000 + (msgs[i].text?.length || 0) * 60);
        if (msgs[i].playerId === pid && Date.now() - msgs[i].timestamp < ttl) return msgs[i];
      }
      return null;
    };

    // smooth remote players — lerp toward last network position instead of snapping
    const lerpMap = {};
    const lerpPos = (sid, tx, ty) => {
      let e = lerpMap[sid];
      if (!e || Math.abs(e.x - tx) > 240 || Math.abs(e.y - ty) > 240) { e = { x: tx, y: ty }; lerpMap[sid] = e; }
      e.x += (tx - e.x) * 0.18;
      e.y += (ty - e.y) * 0.18;
      return e;
    };

    const drawMyAvatar = (x, y) => {
      const walking = walkingRef.current;
      if (walking) walkFrameRef.current += 0.28;
      const wp = walkFrameRef.current;
      if (isSheetSpecial(avatar?.special)) {
        drawSpecialSprite(ctx, avatar.special, x, y - 30, 1, dirRef.current, walking, wp);
      } else {
        const g = resolveGender(avatar?.gender);
        drawPlayerSprite(ctx, x, y, dirRef.current, walking, wp, 1.4, computeTint(avatar), g);
        drawOutfitLayers(ctx, avatar, x, y, dirRef.current, walking, wp, 1.4);
      }
      const em = myEmoteRef.current;
      const emActive = em?.id && em.until > Date.now();
      if (emActive) drawEmoteBubble(x, y, em.id);
      const myChat = chatFor(user?.user_id || 'me');
      if (myChat && !emActive) drawChatBubble(ctx, x, y - 84, myChat.text, myChat.role, false);
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 4;
      ctx.fillText(user?.username || user?.name || 'You', x, y + 14);
      ctx.shadowBlur = 0;
    };

    const drawRemotePlayer = (p, x, y, sid) => {
      const a = p.avatar || {};
      const dir = ['up', 'down', 'left', 'right'].includes(p.direction) ? p.direction : 'down';
      if (isSheetSpecial(a.special)) {
        drawSpecialSprite(ctx, a.special, x, y - 30, 1, dir, p.is_walking, phaseRef.current * 0.2);
      } else {
        const g = resolveGender(a.gender);
        ensureSpriteLoaded(g);
        drawPlayerSprite(ctx, x, y, dir, p.is_walking, phaseRef.current * 0.2, 1.4, computeTint(a), g);
        drawOutfitLayers(ctx, a, x, y, dir, p.is_walking, phaseRef.current * 0.2, 1.4);
      }
      const remoteEmote = p.currentEmote && (p.emoteEndTime || 0) > Date.now();
      if (remoteEmote) drawEmoteBubble(x, y, p.currentEmote);
      const pChat = sid ? chatFor(sid) : null;
      if (pChat && !remoteEmote) drawChatBubble(ctx, x, y - 84, pChat.text, pChat.role, false);
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText(p.name || 'Guest', x, y + 13);
    };

    const drawSoakingAvatar = (x, y, name, a) => {
      const t2 = phaseRef.current * 0.05;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x - 42, y - 88, 84, 62);
      ctx.clip();
      if (isSheetSpecial(a?.special)) {
        drawSpecialSprite(ctx, a.special, x, y - 30, 1, 'down', false, 0);
      } else {
        const g = resolveGender(a?.gender);
        ensureSpriteLoaded(g);
        drawPlayerSprite(ctx, x, y, 'down', false, 0, 1.4, computeTint(a), g);
        drawOutfitLayers(ctx, a, x, y, 'down', false, 0, 1.4);
      }
      ctx.restore();
      // water line over the body
      ctx.fillStyle = 'rgba(56,189,248,0.75)';
      ctx.beginPath(); ctx.ellipse(x, y - 26 + Math.sin(t2 * 2) * 1.5, 27, 7, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.ellipse(x, y - 28, 22, 5, 0, 0, Math.PI * 2); ctx.stroke();
      // relaxing steam rising over the avatar
      for (let i = 0; i < 4; i++) {
        const rise = (phaseRef.current * 0.55 + i * 22) % 70;
        ctx.fillStyle = `rgba(255,255,255,${Math.max(0, 0.3 - rise / 300)})`;
        ctx.beginPath();
        ctx.arc(x - 18 + i * 12 + Math.sin(t2 * 1.6 + i * 1.8) * 7, y - 64 - rise, 6 + rise * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.font = '13px serif';
      ctx.fillStyle = `rgba(255,255,255,${Math.max(0.15, 0.5 + Math.sin(t2 * 3) * 0.4)})`;
      ctx.fillText('♨', x + 28, y - 72 - Math.sin(t2 * 2) * 4);
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 4;
      ctx.fillText(name, x, y + 40);
      ctx.shadowBlur = 0;
    };

    const drawHallway = () => {
      const cam = camRef.current;
      // walls
      const wallGrad = ctx.createLinearGradient(0, 0, W, 0);
      wallGrad.addColorStop(0, '#241a10');
      wallGrad.addColorStop(0.5, '#3a2c1a');
      wallGrad.addColorStop(1, '#241a10');
      ctx.fillStyle = wallGrad;
      ctx.fillRect(0, 0, W, H);
      // carpet
      const carpet = ctx.createLinearGradient(200, 0, 600, 0);
      carpet.addColorStop(0, '#3d1d1d');
      carpet.addColorStop(0.5, '#5b2626');
      carpet.addColorStop(1, '#3d1d1d');
      ctx.fillStyle = carpet;
      ctx.fillRect(200, 0, 400, H);
      ctx.strokeStyle = 'rgba(251,191,36,0.35)';
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(210, 0); ctx.lineTo(210, H); ctx.moveTo(590, 0); ctx.lineTo(590, H); ctx.stroke();
      // carpet pattern
      ctx.fillStyle = 'rgba(251,191,36,0.08)';
      for (let wy = -((cam) % 60); wy < H; wy += 60) {
        ctx.beginPath();
        ctx.moveTo(400, wy); ctx.lineTo(430, wy + 30); ctx.lineTo(400, wy + 60); ctx.lineTo(370, wy + 30);
        ctx.closePath(); ctx.fill();
      }
      // elevator (exit) at world top
      const ey = 60 - cam;
      if (ey > -120 && ey < H) {
        ctx.fillStyle = '#4b5563';
        ctx.fillRect(330, ey, 140, 90);
        ctx.fillStyle = '#9ca3af';
        ctx.fillRect(340, ey + 8, 58, 74);
        ctx.fillRect(402, ey + 8, 58, 74);
        ctx.fillStyle = '#fbbf24';
        ctx.font = 'bold 12px monospace';
        ctx.textAlign = 'center';
        ctx.fillText('⬆ CLUB LOBBY', 400, ey - 6);
      }
      // penthouse door (world top, right of elevator)
      const py = 52 - cam;
      if (py > -170 && py < H) {
        // city exit door (left of elevator)
        const cglow = 0.55 + Math.abs(Math.sin(phaseRef.current * 0.035)) * 0.45;
        ctx.fillStyle = '#0d1420';
        ctx.fillRect(116, py + 8, 168, 108);
        ctx.strokeStyle = `rgba(147,197,253,${cglow})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(116, py + 8, 168, 108);
        // glass double doors showing city night
        ctx.fillStyle = '#0a0e24';
        ctx.fillRect(124, py + 16, 74, 92);
        ctx.fillRect(202, py + 16, 74, 92);
        for (let i = 0; i < 8; i++) {
          ctx.fillStyle = `rgba(253,230,138,${0.3 + ((i * 7) % 10) / 20})`;
          ctx.fillRect(132 + (i * 33) % 136, py + 58 + (i * 13) % 40, 4, 5);
        }
        ctx.fillStyle = '#141a38';
        ctx.fillRect(130, py + 74, 26, 34);
        ctx.fillRect(178, py + 62, 20, 46);
        ctx.fillRect(226, py + 80, 30, 28);
        ctx.fillStyle = '#93c5fd';
        ctx.beginPath();
        ctx.arc(194, py + 66, 3, 0, Math.PI * 2);
        ctx.arc(208, py + 66, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = 'bold 13px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#93c5fd';
        ctx.shadowColor = '#93c5fd';
        ctx.shadowBlur = 10 * cglow;
        ctx.fillText('🌃 CITY EXIT', 200, py - 2);
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(120, 120, 130, 0.5)';
        ctx.fillRect(126, py + 118, 148, 10);
        // ===== penthouse door =====
        const glow = 0.55 + Math.abs(Math.sin(phaseRef.current * 0.03)) * 0.45;
        ctx.fillStyle = '#171007';
        ctx.fillRect(510, py, 176, 116);
        ctx.strokeStyle = `rgba(251,191,36,${glow})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(510, py, 176, 116);
        ctx.fillStyle = '#4a3512';
        ctx.fillRect(518, py + 8, 78, 100);
        ctx.fillRect(600, py + 8, 78, 100);
        ctx.strokeStyle = 'rgba(251,191,36,0.6)';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(526, py + 16, 62, 84);
        ctx.strokeRect(608, py + 16, 62, 84);
        ctx.fillStyle = '#fde047';
        ctx.beginPath();
        ctx.arc(590, py + 62, 4, 0, Math.PI * 2);
        ctx.arc(606, py + 62, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = 'bold 15px serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#FFD700';
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 12 * glow;
        ctx.fillText('👑 PENTHOUSE', 598, py - 8);
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(30,20,5,0.92)';
        ctx.fillRect(534, py + 84, 128, 16);
        ctx.font = 'bold 10px serif';
        if (penthouse?.occupied) {
          ctx.fillStyle = '#FFD700';
          ctx.shadowColor = '#FFD700';
          ctx.shadowBlur = 6;
          ctx.fillText((penthouse.owner_username || '').slice(0, 14).toUpperCase(), 598, py + 96);
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.fillText(isStaff ? 'CLAIM ME, BOSS' : 'THE BOSS ONLY', 598, py + 96);
        }
        ctx.fillStyle = 'rgba(150,20,30,0.85)';
        ctx.fillRect(540, py + 118, 116, 12);
      }
      // doors
      for (let n = 1; n <= 30; n++) {
        const d = doorPos(n);
        const sy = d.y - cam;
        if (sy < -140 || sy > H + 100) continue;
        const info = rooms.find((r) => r.room_number === n);
        const occupied = info?.occupied;
        const mine = occupied && info.owner_user_id === user?.user_id;
        // frame + door
        ctx.fillStyle = '#1c1209';
        ctx.fillRect(d.x - 40, sy - 104, 80, 108);
        ctx.fillStyle = mine ? '#7c5f1d' : occupied ? '#5b422a' : '#43301c';
        ctx.fillRect(d.x - 34, sy - 98, 68, 98);
        ctx.strokeStyle = mine ? '#fbbf24' : 'rgba(251,191,36,0.3)';
        ctx.lineWidth = mine ? 3 : 1.5;
        ctx.strokeRect(d.x - 34, sy - 98, 68, 98);
        // knob
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath();
        ctx.arc(d.x + (d.left ? 22 : -22), sy - 46, 3.5, 0, Math.PI * 2);
        ctx.fill();
        // number plate
        ctx.fillStyle = '#111';
        ctx.fillRect(d.x - 16, sy - 92, 32, 15);
        ctx.fillStyle = '#e5e7eb';
        ctx.font = 'bold 11px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(String(n).padStart(2, '0'), d.x, sy - 81);
        // gold name plaque
        if (occupied) {
          ctx.fillStyle = 'rgba(30,20,5,0.9)';
          ctx.fillRect(d.x - 32, sy - 72, 64, 15);
          ctx.font = 'bold 9px serif';
          ctx.fillStyle = '#FFD700';
          ctx.shadowColor = '#FFD700';
          ctx.shadowBlur = 6;
          const nm = (info.owner_username || '').slice(0, 12).toUpperCase();
          ctx.fillText(nm, d.x, sy - 61);
          ctx.shadowBlur = 0;
        } else {
          ctx.font = 'italic 9px sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.45)';
          ctx.fillText('VACANT — 100 VC', d.x, sy - 63);
        }
        // welcome mat
        ctx.fillStyle = 'rgba(120,80,40,0.7)';
        ctx.fillRect(d.x - 22, sy + 6, 44, 10);
      }
      // wall lamps between rows
      for (let rIdx = 0; rIdx <= DOOR_ROWS; rIdx++) {
        const ly = ROW_START - 70 + rIdx * ROW_SPACING - cam;
        if (ly < -30 || ly > H + 30) continue;
        [64, 736].forEach((lx) => {
          const gl = 0.5 + Math.abs(Math.sin(phaseRef.current * 0.02 + rIdx + lx)) * 0.5;
          ctx.fillStyle = `rgba(253, 230, 138, ${gl * 0.9})`;
          ctx.beginPath();
          ctx.arc(lx, ly, 6, Math.PI, 0);
          ctx.fill();
          ctx.fillStyle = `rgba(253, 230, 138, ${gl * 0.12})`;
          ctx.beginPath();
          ctx.arc(lx, ly, 30, 0, Math.PI * 2);
          ctx.fill();
        });
      }
      // remote players in the hallway
      const cur = playersRef.current || {};
      Object.entries(cur).forEach(([sid, p]) => {
        if ((p.room || 'main') !== 'apartments' || !p.x) return;
        const lp = lerpPos(sid, p.x, p.y);
        drawRemotePlayer(p, lp.x, lp.y - cam, sid);
      });
      // me
      drawMyAvatar(posRef.current.x, posRef.current.y - cam);
      // header
      const hs = H > BASE_H ? 1.6 : 1;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, 34 * hs);
      ctx.fillStyle = '#fbbf24';
      ctx.font = `bold ${Math.round(15 * hs)}px monospace`;
      ctx.textAlign = 'center';
      ctx.shadowColor = '#fbbf24';
      ctx.shadowBlur = 8;
      ctx.fillText('🏢 RAZIENT APARTMENTS', W / 2, 22 * hs);
      ctx.shadowBlur = 0;
    };

    const drawInterior = () => {
      const itr = interiorRef.current;
      if (!itr) return;
      const cfg = SUB_ROOMS[itr.subRoom];
      // walls + floor (extended to fill tall/portrait canvases)
      ctx.fillStyle = cfg.wall;
      ctx.fillRect(0, 0, W, offY + 300);
      ctx.fillStyle = cfg.floor;
      ctx.fillRect(0, offY + 300, W, H - offY - 300);
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth = 1;
      for (let fx = 0; fx < W + 60; fx += 50) {
        ctx.beginPath(); ctx.moveTo(fx, offY + 300); ctx.lineTo(fx - 40, H); ctx.stroke();
      }
      // scene is authored in 800x520 coords — center it vertically
      ctx.save();
      ctx.translate(0, offY);
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillRect(0, 296, W, 6);
      const t = phaseRef.current;

      if (itr.subRoom === 'bedroom') {
        // window with night sky
        ctx.fillStyle = '#0b1026';
        ctx.fillRect(560, 90, 160, 120);
        for (let i = 0; i < 14; i++) {
          ctx.fillStyle = `rgba(255,255,255,${0.4 + Math.abs(Math.sin(t * 0.02 + i)) * 0.5})`;
          ctx.fillRect(570 + (i * 37) % 140, 100 + (i * 23) % 100, 2, 2);
        }
        ctx.strokeStyle = '#78350f'; ctx.lineWidth = 5;
        ctx.strokeRect(560, 90, 160, 120);
        ctx.beginPath(); ctx.moveTo(640, 90); ctx.lineTo(640, 210); ctx.stroke();
        // bed (built-in)
        ctx.fillStyle = '#78350f';
        ctx.fillRect(300, 330, 160, 90);
        ctx.fillStyle = '#7c3aed';
        ctx.fillRect(306, 336, 148, 60);
        ctx.fillStyle = '#e5e7eb';
        ctx.fillRect(306, 336, 44, 30);
        ctx.fillStyle = '#5b21b6';
        ctx.fillRect(306, 372, 148, 24);
        ctx.fillStyle = '#78350f';
        ctx.fillRect(296, 322, 168, 12);
        // bathroom door (left wall)
        ctx.fillStyle = '#43301c';
        ctx.fillRect(56, 165, 68, 130);
        ctx.strokeStyle = 'rgba(251,191,36,0.5)';
        ctx.strokeRect(56, 165, 68, 130);
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath(); ctx.arc(112, 232, 3.5, 0, Math.PI * 2); ctx.fill();
        ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center'; ctx.fillStyle = '#fde68a';
        ctx.fillText('BATHROOM', 90, 158);
      } else if (itr.subRoom === 'bathroom') {
        // tub
        ctx.fillStyle = '#e5e7eb';
        ctx.beginPath(); ctx.ellipse(600, 380, 90, 34, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#7dd3fc';
        ctx.beginPath(); ctx.ellipse(600, 376, 76, 24, 0, 0, Math.PI * 2); ctx.fill();
        for (let i = 0; i < 4; i++) {
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.beginPath();
          ctx.arc(560 + (i * 31) % 90, 372 + Math.sin(t * 0.06 + i) * 5, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
        // sink + mirror
        ctx.fillStyle = '#d1d5db'; ctx.fillRect(300, 250, 70, 40);
        ctx.fillStyle = '#9ca3af'; ctx.fillRect(310, 290, 50, 30);
        ctx.fillStyle = '#a5f3fc'; ctx.fillRect(302, 130, 66, 90);
        ctx.strokeStyle = '#6b7280'; ctx.strokeRect(302, 130, 66, 90);
        // toilet
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(170, 330, 44, 30);
        ctx.beginPath(); ctx.ellipse(192, 372, 26, 16, 0, 0, Math.PI * 2); ctx.fill();
        // door back to bedroom
        ctx.fillStyle = '#43301c'; ctx.fillRect(56, 165, 68, 130);
        ctx.strokeStyle = 'rgba(251,191,36,0.5)'; ctx.strokeRect(56, 165, 68, 130);
        ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center'; ctx.fillStyle = '#fde68a';
        ctx.fillText('BEDROOM', 90, 158);
      } else if (itr.subRoom === 'kitchen') {
        // counters
        ctx.fillStyle = '#5b422a'; ctx.fillRect(80, 240, 300, 60);
        ctx.fillStyle = '#7c5c38'; ctx.fillRect(80, 234, 300, 10);
        // stove
        ctx.fillStyle = '#374151'; ctx.fillRect(160, 244, 70, 50);
        ctx.fillStyle = '#ef4444';
        ctx.beginPath(); ctx.arc(180, 256, 8, 0, Math.PI * 2); ctx.arc(210, 256, 8, 0, Math.PI * 2); ctx.fill();
        // fridge
        ctx.fillStyle = '#cbd5e1'; ctx.fillRect(600, 160, 90, 150);
        ctx.fillStyle = '#94a3b8'; ctx.fillRect(600, 220, 90, 4);
        ctx.fillStyle = '#475569'; ctx.fillRect(680, 180, 5, 26); ctx.fillRect(680, 236, 5, 26);
        // hanging pans
        ctx.strokeStyle = '#9ca3af';
        [300, 330, 360].forEach((hx, i) => {
          ctx.beginPath(); ctx.moveTo(hx, 130); ctx.lineTo(hx, 150 + i * 4); ctx.stroke();
          ctx.fillStyle = '#6b7280';
          ctx.beginPath(); ctx.arc(hx, 158 + i * 4, 8, 0, Math.PI * 2); ctx.fill();
        });
      } else if (itr.subRoom === 'living') {
        // fireplace built-in
        ctx.fillStyle = '#4b3621'; ctx.fillRect(330, 170, 140, 130);
        ctx.fillStyle = '#111'; ctx.fillRect(355, 210, 90, 80);
        const fh = 22 + Math.abs(Math.sin(t * 0.09)) * 14;
        const fg = ctx.createLinearGradient(400, 290, 400, 290 - fh);
        fg.addColorStop(0, 'rgba(255,90,0,0.95)');
        fg.addColorStop(1, 'rgba(255,220,90,0.5)');
        ctx.fillStyle = fg;
        ctx.beginPath();
        ctx.moveTo(370, 290);
        ctx.quadraticCurveTo(385, 290 - fh * 0.8, 400, 290 - fh);
        ctx.quadraticCurveTo(415, 290 - fh * 0.7, 430, 290);
        ctx.fill();
        ctx.fillStyle = '#78350f'; ctx.fillRect(322, 162, 156, 12);
      } else if (itr.subRoom === 'penthouse_main') {
        // grand skyline window
        ctx.fillStyle = '#0a0e24';
        ctx.fillRect(70, 70, 420, 180);
        for (let i = 0; i < 26; i++) {
          ctx.fillStyle = `rgba(255,255,255,${0.3 + Math.abs(Math.sin(t * 0.02 + i * 1.7)) * 0.5})`;
          ctx.fillRect(80 + (i * 53) % 400, 82 + (i * 31) % 90, 2, 2);
        }
        ctx.fillStyle = '#141a38';
        [[90, 110, 58], [172, 84, 44], [240, 128, 66], [330, 92, 52], [406, 118, 60]].forEach(([bx, bh, bw]) => {
          ctx.fillRect(bx, 250 - bh, bw, bh);
        });
        for (let i = 0; i < 30; i++) {
          ctx.fillStyle = `rgba(253,224,71,${0.25 + ((i * 7) % 10) / 18})`;
          ctx.fillRect(96 + (i * 41) % 370, 160 + (i * 17) % 82, 3, 3);
        }
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 6;
        ctx.strokeRect(70, 70, 420, 180);
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(280, 70); ctx.lineTo(280, 250); ctx.stroke();
        // chandelier
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(560, 0); ctx.lineTo(560, 55); ctx.stroke();
        ctx.beginPath(); ctx.arc(560, 68, 22, Math.PI, 0); ctx.stroke();
        [-20, -10, 0, 10, 20].forEach((ox, i) => {
          const gl = 0.5 + Math.abs(Math.sin(t * 0.05 + i)) * 0.5;
          ctx.fillStyle = `rgba(253,230,138,${gl})`;
          ctx.beginPath(); ctx.arc(560 + ox, 72, 4, 0, Math.PI * 2); ctx.fill();
        });
        ctx.fillStyle = 'rgba(253,230,138,0.07)';
        ctx.beginPath(); ctx.arc(560, 80, 70, 0, Math.PI * 2); ctx.fill();
        // gold fireplace
        ctx.fillStyle = '#3a2a10'; ctx.fillRect(505, 185, 110, 115);
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 3; ctx.strokeRect(505, 185, 110, 115);
        ctx.fillStyle = '#0a0603'; ctx.fillRect(522, 218, 76, 74);
        const fh2 = 20 + Math.abs(Math.sin(t * 0.09)) * 14;
        const fg2 = ctx.createLinearGradient(560, 292, 560, 292 - fh2);
        fg2.addColorStop(0, 'rgba(255,90,0,0.95)');
        fg2.addColorStop(1, 'rgba(255,220,90,0.5)');
        ctx.fillStyle = fg2;
        ctx.beginPath();
        ctx.moveTo(534, 292);
        ctx.quadraticCurveTo(548, 292 - fh2 * 0.8, 560, 292 - fh2);
        ctx.quadraticCurveTo(572, 292 - fh2 * 0.7, 586, 292);
        ctx.fill();
        // private hot tub (left floor)
        ctx.fillStyle = '#d9cdb8';
        ctx.beginPath(); ctx.ellipse(185, 415, 92, 34, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.ellipse(185, 415, 92, 34, 0, 0, Math.PI * 2); ctx.stroke();
        const wg2 = ctx.createLinearGradient(100, 395, 270, 435);
        wg2.addColorStop(0, '#38bdf8'); wg2.addColorStop(1, '#0369a1');
        ctx.fillStyle = wg2;
        ctx.beginPath(); ctx.ellipse(185, 412, 74, 24, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1.5;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.ellipse(185, 412, 20 + ((t * 0.6 + i * 18) % 54), 7 + ((t * 0.6 + i * 18) % 54) * 0.3, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        for (let i = 0; i < 6; i++) {
          const bub = (t * 0.9 + i * 23) % 30;
          ctx.fillStyle = `rgba(255,255,255,${0.7 - bub / 45})`;
          ctx.beginPath();
          ctx.arc(140 + (i * 19) % 92 + Math.sin(t * 0.05 + i) * 4, 420 - bub * 0.5, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
        for (let i = 0; i < 3; i++) {
          const sy2 = (t * 0.5 + i * 26) % 60;
          ctx.fillStyle = `rgba(255,255,255,${0.16 - sy2 / 520})`;
          ctx.beginPath();
          ctx.arc(150 + i * 38 + Math.sin(t * 0.03 + i * 2) * 8, 396 - sy2, 11 + sy2 * 0.16, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = '#8a6d3b'; ctx.fillRect(150, 444, 70, 9);
        // cozy seat cushions in the tub
        TUB_SEATS.forEach((s) => {
          ctx.fillStyle = 'rgba(253,230,138,0.2)';
          ctx.beginPath(); ctx.ellipse(s.x, s.y - 6, 15, 6, 0, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = 'rgba(253,230,138,0.4)'; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.ellipse(s.x, s.y - 6, 15, 6, 0, 0, Math.PI * 2); ctx.stroke();
        });
        if (!soakingRef.current) {
          ctx.font = '10px sans-serif'; ctx.textAlign = 'center';
          ctx.fillStyle = `rgba(255,255,255,${0.35 + Math.abs(Math.sin(t * 0.04)) * 0.25})`;
          ctx.fillText('tap the tub to soak ♨ · 3 cozy seats', 185, 366);
        }
        // grand staircase going UP (right side)
        ctx.fillStyle = '#2c1f0c';
        for (let s = 0; s < 7; s++) {
          ctx.fillRect(644 + s * 18, 300 - s * 19, 146 - s * 18, 16);
        }
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(650, 292); ctx.lineTo(766, 172); ctx.stroke();
        [0, 1, 2, 3].forEach((i) => {
          ctx.beginPath(); ctx.moveTo(660 + i * 32, 300 - i * 33); ctx.lineTo(660 + i * 32, 284 - i * 33); ctx.stroke();
        });
        ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#fde68a';
        ctx.shadowColor = '#fbbf24'; ctx.shadowBlur = 8;
        ctx.fillText('UPSTAIRS ▲', 712, 158);
        ctx.shadowBlur = 0;
      } else if (itr.subRoom === 'penthouse_upper') {
        // skylight with stars
        ctx.fillStyle = '#0a0e24';
        ctx.beginPath(); ctx.ellipse(300, 62, 190, 46, 0, 0, Math.PI * 2); ctx.fill();
        for (let i = 0; i < 16; i++) {
          ctx.fillStyle = `rgba(255,255,255,${0.35 + Math.abs(Math.sin(t * 0.02 + i * 2.1)) * 0.55})`;
          ctx.fillRect(140 + (i * 43) % 330, 42 + (i * 13) % 42, 2, 2);
        }
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 5;
        ctx.beginPath(); ctx.ellipse(300, 62, 190, 46, 0, 0, Math.PI * 2); ctx.stroke();
        // king bed with gold headboard (right of center)
        ctx.fillStyle = '#b8860b'; ctx.fillRect(400, 210, 220, 18);
        ctx.fillStyle = '#4a2c0a'; ctx.fillRect(406, 228, 208, 100);
        ctx.fillStyle = '#7c1d3f'; ctx.fillRect(412, 234, 196, 66);
        ctx.fillStyle = '#f3e8d2'; ctx.fillRect(412, 234, 58, 30); ctx.fillRect(478, 234, 58, 30);
        ctx.fillStyle = '#5b1030'; ctx.fillRect(412, 302, 196, 24);
        // ===== BALCONY DJ DECK (left) =====
        // open night sky behind the rail
        ctx.fillStyle = '#0a0e24';
        ctx.fillRect(30, 148, 300, 64);
        for (let i = 0; i < 12; i++) {
          ctx.fillStyle = `rgba(255,255,255,${0.3 + Math.abs(Math.sin(t * 0.02 + i * 1.3)) * 0.5})`;
          ctx.fillRect(40 + (i * 29) % 280, 154 + (i * 17) % 52, 2, 2);
        }
        ctx.fillStyle = '#141a38';
        [[46, 40, 34], [112, 30, 26], [170, 46, 40], [238, 34, 30], [292, 42, 28]].forEach(([bx, bh, bw]) => {
          ctx.fillRect(bx, 212 - bh, bw, bh);
        });
        ctx.font = 'bold 10px sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#fde68a';
        ctx.shadowColor = '#fbbf24'; ctx.shadowBlur = 7;
        ctx.fillText('🎧 BALCONY DJ DECK', 180, 142);
        ctx.shadowBlur = 0;
        // gold rail across the balcony
        ctx.strokeStyle = '#b8860b'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(30, 212); ctx.lineTo(330, 212); ctx.stroke();
        for (let rx = 38; rx <= 326; rx += 24) {
          ctx.beginPath(); ctx.moveTo(rx, 212); ctx.lineTo(rx, 252); ctx.stroke();
        }
        // DJ booth with spinning turntables
        const boothGlow = `hsl(${(phaseRef.current * 1.4) % 360}, 90%, 58%)`;
        ctx.fillStyle = '#160e26'; ctx.fillRect(80, 258, 200, 48);
        ctx.strokeStyle = boothGlow; ctx.lineWidth = 2.5;
        ctx.strokeRect(80, 258, 200, 48);
        [125, 235].forEach((tx, ti) => {
          ctx.fillStyle = '#0a0a0a';
          ctx.beginPath(); ctx.arc(tx, 282, 17, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#4b5563'; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(tx, 282, 12, 0, Math.PI * 2); ctx.stroke();
          const ang = t * 0.18 + ti * 1.5;
          ctx.strokeStyle = boothGlow; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(tx, 282); ctx.lineTo(tx + Math.cos(ang) * 13, 282 + Math.sin(ang) * 13); ctx.stroke();
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath(); ctx.arc(tx, 282, 2.5, 0, Math.PI * 2); ctx.fill();
        });
        ctx.fillStyle = '#241636'; ctx.fillRect(162, 268, 56, 28);
        [172, 190, 208].forEach((kx) => {
          ctx.fillStyle = boothGlow;
          ctx.beginPath(); ctx.arc(kx, 276, 3, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#6b7280'; ctx.fillRect(kx - 1.5, 284, 3, 9);
        });
        // EQ bars above the booth
        for (let i = 0; i < 9; i++) {
          const bh2 = 5 + Math.abs(Math.sin(t * 0.11 + i * 0.9)) * 20;
          ctx.fillStyle = `hsla(${(i * 36 + phaseRef.current * 2) % 360}, 90%, 60%, 0.85)`;
          ctx.fillRect(96 + i * 20, 254 - bh2, 10, bh2);
        }
        // flanking speakers with pumping cones
        [[48, 254], [288, 254]].forEach(([sx2, sy3], si) => {
          ctx.fillStyle = '#111'; ctx.fillRect(sx2, sy3, 30, 52);
          ctx.strokeStyle = '#333'; ctx.strokeRect(sx2, sy3, 30, 52);
          const pump = 1 + Math.abs(Math.sin(t * 0.12 + si)) * 0.25;
          ctx.fillStyle = '#374151';
          ctx.beginPath(); ctx.arc(sx2 + 15, sy3 + 18, 8 * pump, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(sx2 + 15, sy3 + 40, 5 * pump, 0, Math.PI * 2); ctx.fill();
        });
        // floating music notes
        for (let i = 0; i < 3; i++) {
          const rise = (t * 0.7 + i * 34) % 90;
          ctx.font = '14px serif';
          ctx.fillStyle = `rgba(253,224,71,${0.85 - rise / 110})`;
          ctx.fillText(i % 2 ? '♫' : '♪', 120 + i * 60 + Math.sin(t * 0.04 + i * 2) * 12, 250 - rise);
        }
        // staircase going DOWN (right side)
        ctx.fillStyle = '#2c1f0c';
        for (let s = 0; s < 7; s++) {
          ctx.fillRect(644 + s * 18, 176 + s * 19, 146 - s * 18, 16);
        }
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(650, 184); ctx.lineTo(766, 304); ctx.stroke();
        ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#fde68a';
        ctx.shadowColor = '#fbbf24'; ctx.shadowBlur = 8;
        ctx.fillText('DOWNSTAIRS ▼', 712, 168);
        ctx.shadowBlur = 0;
      }

      // exit door mat (entry rooms) — walk onto it to leave
      if (itr.subRoom === 'bedroom' || itr.subRoom === 'penthouse_main') {
        const eg = 0.5 + Math.abs(Math.sin(t * 0.045)) * 0.5;
        ctx.fillStyle = 'rgba(6,38,26,0.88)';
        ctx.fillRect(336, 448, 128, 38);
        ctx.strokeStyle = `rgba(52,211,153,${eg})`;
        ctx.lineWidth = 2.5;
        ctx.strokeRect(336, 448, 128, 38);
        ctx.font = 'bold 12px monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = `rgba(110,231,183,${0.55 + eg * 0.45})`;
        ctx.shadowColor = '#34d399';
        ctx.shadowBlur = 9 * eg;
        ctx.fillText('▼ EXIT ▼', 400, 464);
        ctx.shadowBlur = 0;
        ctx.font = '9px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.fillText('to hallway', 400, 478);
      }

      // placed furniture (sorted by y for depth)
      const placed = (itr.furniture || [])
        .filter((f) => f.sub_room === itr.subRoom)
        .sort((a, b) => a.y - b.y);
      placed.forEach((f) => drawFurniture(ctx, f.item_id, f.x, f.y, phaseRef.current, editRef.current.editMode));

      // remote players in this exact sub-room
      const roomId = `apt_${itr.roomNumber}_${itr.subRoom}`;
      const tubZone = (px, py2) => itr.subRoom === 'penthouse_main' && Math.abs(px - 185) < 95 && Math.abs(py2 - 412) < 40;
      const drawables = Object.entries(playersRef.current || {})
        .filter(([, p]) => (p.room || 'main') === roomId && p.x)
        .map(([sid, p]) => {
          const lp = lerpPos(sid, p.x, p.y);
          return {
            y: lp.y,
            fn: () => (tubZone(lp.x, lp.y)
              ? drawSoakingAvatar(lp.x, lp.y, p.name || 'Guest', p.avatar || {})
              : drawRemotePlayer(p, lp.x, lp.y, sid)),
          };
        });
      drawables.push({
        y: posRef.current.y,
        fn: () => (soakingRef.current && itr.subRoom === 'penthouse_main'
          ? drawSoakingAvatar(posRef.current.x, posRef.current.y, user?.username || user?.name || 'You', avatar || {})
          : drawMyAvatar(posRef.current.x, posRef.current.y)),
      });
      drawables.sort((a, b) => a.y - b.y);
      drawables.forEach((d) => d.fn());
      ctx.restore();

      // header: apt number + gold owner name
      const hs2 = H > BASE_H ? 1.6 : 1;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, 40 * hs2);
      ctx.font = `bold ${Math.round(14 * hs2)}px monospace`;
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.fillText(itr.isPenthouse ? cfg.name.toUpperCase() : `APT ${String(itr.roomNumber).padStart(2, '0')} — ${cfg.name}`, W / 2, 17 * hs2);
      ctx.font = `bold ${Math.round(11 * hs2)}px serif`;
      ctx.fillStyle = '#FFD700';
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 6;
      ctx.fillText(`★ ${(itr.ownerName || '').toUpperCase()} ★`, W / 2, 33 * hs2);
      ctx.shadowBlur = 0;
      if (editRef.current.editMode) {
        ctx.fillStyle = 'rgba(251,191,36,0.9)';
        ctx.font = `bold ${Math.round(11 * hs2)}px sans-serif`;
        ctx.fillText(
          editRef.current.placingItem ? 'Tap anywhere to place your item' : 'Edit mode: tap an item to pick it up, or select furniture below',
          W / 2, H - 10
        );
      }
    };

    const loop = () => {
      phaseRef.current += 1;
      // movement
      const target = targetRef.current;
      if (target) {
        const dx = target.x - posRef.current.x;
        const dy = target.y - posRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 3) {
          const sp = 2.4;
          posRef.current = { x: posRef.current.x + (dx / dist) * sp, y: posRef.current.y + (dy / dist) * sp };
          const dir = directionFromDelta(dx, dy);
          if (dir) dirRef.current = dir;
          walkingRef.current = true;
          const now = Date.now();
          if (now - lastSendRef.current > 50 && socketRef?.current?.connected) {
            lastSendRef.current = now;
            seqRef.current++;
            socketRef.current.emit('player_move', {
              seq: seqRef.current,
              x: Math.round(posRef.current.x),
              y: Math.round(posRef.current.y),
              is_walking: true,
              direction: dirRef.current,
            });
          }
        } else {
          targetRef.current = null;
          walkingRef.current = false;
          socketRef?.current?.emit('player_move', {
            seq: ++seqRef.current,
            x: Math.round(posRef.current.x),
            y: Math.round(posRef.current.y),
            is_walking: false,
            direction: dirRef.current,
          });
        }
      } else {
        walkingRef.current = false;
      }
      // keyboard (WASD/arrows) — takes precedence over click-to-move
      {
        const keys = keysRef.current;
        const kx = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
        const ky = (keys.down ? 1 : 0) - (keys.up ? 1 : 0);
        if (kx || ky) {
          if (soakingRef.current && Date.now() - soakEnterRef.current > 800) soakingRef.current = false;
          if (!soakingRef.current) {
            targetRef.current = null;
            const n = Math.hypot(kx, ky);
            const sp = 2.4;
            let nx2 = posRef.current.x + (kx / n) * sp;
            let ny2 = posRef.current.y + (ky / n) * sp;
            if (modeRef.current === 'hallway') {
              nx2 = Math.max(CORRIDOR.minX, Math.min(CORRIDOR.maxX, nx2));
              ny2 = Math.max(140, Math.min(WORLD_H - 40, ny2));
            } else {
              nx2 = Math.max(70, Math.min(730, nx2));
              ny2 = Math.max(300, Math.min(480, ny2));
            }
            posRef.current = { x: nx2, y: ny2 };
            const kdir = directionFromDelta(kx, ky);
            if (kdir) dirRef.current = kdir;
            walkingRef.current = true;
            keyWalkRef.current = true;
            const nowK = Date.now();
            if (nowK - lastSendRef.current > 50 && socketRef?.current?.connected) {
              lastSendRef.current = nowK;
              socketRef.current.emit('player_move', {
                seq: ++seqRef.current, x: Math.round(nx2), y: Math.round(ny2), is_walking: true, direction: dirRef.current,
              });
            }
          }
        } else if (keyWalkRef.current) {
          keyWalkRef.current = false;
          walkingRef.current = false;
          socketRef?.current?.emit('player_move', {
            seq: ++seqRef.current, x: Math.round(posRef.current.x), y: Math.round(posRef.current.y), is_walking: false, direction: dirRef.current,
          });
        }
      }
      // stepping onto the exit mat walks you out the front door
      if (modeRef.current === 'interior') {
        const eItr = interiorRef.current;
        const entry = eItr?.isPenthouse ? 'penthouse_main' : 'bedroom';
        if (eItr && eItr.subRoom === entry && !soakingRef.current && Math.abs(posRef.current.x - 400) < 55 && posRef.current.y > 448) {
          exitToHallway();
        }
      }
      // camera follow (hallway only)
      if (modeRef.current === 'hallway') {
        const targetCam = Math.max(0, Math.min(WORLD_H - H, posRef.current.y - H * 0.45));
        camRef.current += (targetCam - camRef.current) * 0.12;
      }
      ctx.clearRect(0, 0, W, H);
      if (modeRef.current === 'hallway') drawHallway();
      else drawInterior();
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [rooms, penthouse, avatar, user]);

  const placedIds = (interior?.furniture || []).map((f) => f.item_id);
  const inventory = ownedItems.filter((id) => !placedIds.includes(id));
  const daysLeft = interior?.expiresAt
    ? Math.max(0, Math.ceil((new Date(interior.expiresAt) - Date.now()) / 86400000))
    : null;

  return (
    <div className="relative mx-auto" style={{ aspectRatio: `${W} / ${H}`, width: `min(100%, calc((100vh - 70px) * ${(W / H).toFixed(4)}))` }} data-testid="apartment-complex">
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        onClick={(e) => { if (Date.now() - lastTouchRef.current < 700) return; handleClick(e); }}
        onTouchStart={(e) => { lastTouchRef.current = Date.now(); handleClick(e); }}
        className="w-full h-full cursor-pointer touch-none"
        data-testid="apartment-canvas"
      />

      {/* Top-right controls */}
      <div className="absolute top-2 right-2 flex items-center gap-2">
        <span className="bg-black/60 text-yellow-400 text-xs px-2 py-1 rounded-full flex items-center gap-1" data-testid="apartment-vibecoins">
          <Coins className="w-3 h-3" /> {vibecoins}
        </span>
        {mode === 'hallway' ? (
          <Button size="sm" variant="ghost" className="bg-black/60 text-white text-xs h-7" onClick={() => onExit?.()} data-testid="apartment-exit-btn">
            <LogOut className="w-3 h-3 mr-1" /> Club
          </Button>
        ) : (
          <Button size="sm" variant="ghost" className="bg-black/60 text-white text-xs h-7" onClick={exitToHallway} data-testid="apartment-leave-room-btn">
            <DoorOpen className="w-3 h-3 mr-1" /> Hallway
          </Button>
        )}
      </div>

      {/* Interior controls */}
      {mode === 'interior' && interior && (
        <>
          {/* sub-room arrows */}
          <div className="absolute inset-y-0 left-1 flex items-center">
            {interior.subRoom === 'living' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSubRoom('bedroom')} data-testid="arrow-to-bedroom">
                <ArrowLeft className="w-4 h-4 mr-1" /> Bedroom
              </Button>
            )}
            {interior.subRoom === 'kitchen' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSubRoom('living')} data-testid="arrow-to-living-left">
                <ArrowLeft className="w-4 h-4 mr-1" /> Living Room
              </Button>
            )}
          </div>
          <div className="absolute inset-y-0 right-1 flex items-center">
            {interior.subRoom === 'penthouse_main' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-yellow-300 h-9" onClick={() => goSubRoom('penthouse_upper')} data-testid="stairs-up-btn">
                Upstairs <ArrowUp className="w-4 h-4 ml-1" />
              </Button>
            )}
            {interior.subRoom === 'penthouse_upper' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-yellow-300 h-9" onClick={() => goSubRoom('penthouse_main')} data-testid="stairs-down-btn">
                Downstairs <ArrowDown className="w-4 h-4 ml-1" />
              </Button>
            )}
            {interior.subRoom === 'bedroom' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSubRoom('living')} data-testid="arrow-to-living">
                Living Room <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {interior.subRoom === 'living' && (
              <Button size="sm" className="bg-black/70 hover:bg-black/90 text-white h-9" onClick={() => goSubRoom('kitchen')} data-testid="arrow-to-kitchen">
                Kitchen <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>

          {/* owner tools */}
          {interior.isOwner && (
            <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-2">
              <Button size="sm" className={`h-8 text-xs ${editMode ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-black/70 text-white'}`} onClick={() => { setEditMode(!editMode); setPlacingItem(null); }} data-testid="room-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> {editMode ? 'Done Editing' : 'Edit Room'}
              </Button>
              <Button size="sm" className="h-8 text-xs bg-black/70 text-white" onClick={() => setShowShop(true)} data-testid="furniture-shop-btn">
                <Sofa className="w-3 h-3 mr-1" /> Furniture Shop
              </Button>
              {!interior.isPenthouse && (
                <Button size="sm" className="h-8 text-xs bg-black/70 text-emerald-300" onClick={handleRenew} data-testid="renew-lease-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Renew (100)
                </Button>
              )}
              {daysLeft !== null && (
                <span className={`text-[11px] px-2 py-1 rounded-full ${daysLeft <= 1 ? 'bg-red-500/80 text-white' : 'bg-black/60 text-white/80'}`} data-testid="lease-days-left">
                  {daysLeft}d left
                </span>
              )}
            </div>
          )}

          {/* edit inventory bar */}
          {editMode && interior.isOwner && (
            <div className="absolute bottom-12 left-2 right-2 bg-black/80 rounded-lg p-2 flex gap-2 overflow-x-auto" data-testid="furniture-inventory-bar">
              {inventory.length === 0 ? (
                <span className="text-white/50 text-xs py-1">No unplaced furniture — visit the shop!</span>
              ) : inventory.map((id) => {
                const item = FURNITURE.find((f) => f.id === id);
                const meta = RARITY_META[item?.rarity || 'common'];
                return (
                  <button
                    key={id}
                    onClick={() => setPlacingItem(placingItem === id ? null : id)}
                    className={`flex-shrink-0 px-2 py-1 rounded border text-xs ${placingItem === id ? 'border-amber-400 bg-amber-500/20 text-amber-300' : 'border-white/20 text-white/80 hover:border-white/50'}`}
                    style={{ borderColor: placingItem === id ? undefined : `${meta.color}66` }}
                    data-testid={`inventory-${id}`}
                  >
                    {item?.name || id}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Rent modal */}
      {rentPrompt && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-xl z-20" onClick={(e) => e.target === e.currentTarget && setRentPrompt(null)}>
          <div className="bg-gray-900 border border-yellow-500/40 rounded-xl p-5 max-w-xs text-center" data-testid="rent-modal">
            <DoorOpen className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <h3 className="text-white font-bold text-lg">Rent Apt {String(rentPrompt).padStart(2, '0')}?</h3>
            <p className="text-white/60 text-sm mt-1">
              100 VibeCoins for a 1-week lease. Your name goes on the door in gold. Renew anytime before it expires!
            </p>
            {myApt && (
              <p className="text-red-400 text-xs mt-2">You already rent Apt {myApt.room_number} — one room per player.</p>
            )}
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setRentPrompt(null)} data-testid="rent-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                disabled={!!myApt || vibecoins < 100}
                onClick={() => handleRent(rentPrompt)}
                data-testid="rent-confirm-btn"
              >
                <Coins className="w-3 h-3 mr-1" /> Rent — 100
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Penthouse claim modal */}
      {claimPrompt && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20" onClick={(e) => e.target === e.currentTarget && setClaimPrompt(false)}>
          <div className="bg-gray-900 border border-yellow-500/60 rounded-xl p-5 max-w-xs text-center" data-testid="claim-modal">
            <Crown className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <h3 className="text-white font-bold text-lg">Claim the Penthouse?</h3>
            <p className="text-white/60 text-sm mt-1">
              Two floors of pure luxury at the top of the tower. Free for the boss — yours forever. Visitors can still knock.
            </p>
            <div className="flex gap-2 mt-4 justify-center">
              <Button size="sm" variant="outline" onClick={() => setClaimPrompt(false)} data-testid="claim-cancel-btn">Not yet</Button>
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold" onClick={handleClaim} data-testid="claim-penthouse-btn">
                <Crown className="w-3 h-3 mr-1" /> Claim it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Furniture shop */}
      {showShop && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center rounded-xl z-20 p-3" onClick={(e) => e.target === e.currentTarget && setShowShop(false)}>
          <div className="bg-gray-900 border border-purple-500/40 rounded-xl p-4 w-full max-w-lg max-h-[90%] overflow-y-auto" data-testid="furniture-shop-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold flex items-center gap-2"><Sofa className="w-4 h-4 text-purple-400" /> Furniture Shop</h3>
              <button onClick={() => setShowShop(false)} className="text-white/60 hover:text-white" data-testid="close-furniture-shop"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FURNITURE.map((item) => {
                const owned = ownedItems.includes(item.id);
                const meta = RARITY_META[item.rarity];
                return (
                  <div key={item.id} className="rounded-lg border border-white/15 p-2 flex flex-col items-center gap-1" data-testid={`shop-${item.id}`}>
                    <FurnitureThumb id={item.id} />
                    <span className="text-[11px] text-white/90 text-center leading-tight">{item.name}</span>
                    <span className="text-[8px] px-1.5 rounded-full font-bold uppercase" style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.color}55` }}>
                      {meta.label}
                    </span>
                    {owned ? (
                      <span className="text-[10px] text-emerald-400">Owned</span>
                    ) : (
                      <Button size="sm" className="h-6 text-[10px] bg-purple-600 hover:bg-purple-500 px-2" disabled={vibecoins < item.price} onClick={() => handleBuyFurniture(item)} data-testid={`buy-${item.id}`}>
                        <Coins className="w-2.5 h-2.5 mr-0.5" /> {item.price}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Small animated furniture preview
const FurnitureThumb = ({ id }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      drawFurniture(ctx, id, 50, 78, Date.now() * 0.02, false);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [id]);
  return <canvas ref={ref} width={100} height={90} className="w-16 h-14" style={{ imageRendering: 'pixelated' }} />;
};
